// The picker's active-wave scanning UI.
//
// User-confirmed UX rules:
//   1. Hero shows Location, item info, Qty (never changes shape) for the
//      pick phases. The very first phase (awaiting-first-tote) replaces
//      the hero with a simplified Boxes + SCAN TOTE prompt — items can't
//      land anywhere until the picker opens a tote.
//   2. Next location is revealed only after every item at the current
//      location is scanned.
//   3. Slim 8px progress bar at the top, mirroring Dispatch.
//   4. Audio: playBoxAdd on item ok / location ok / tote open;
//      playSuccess on item-line complete; playToteFinalised on tote
//      close + wave complete; playError on every reject.
//
// State machine (single union, drives only sub-prompt + accepted scans):
//   awaiting-first-tote → awaiting-location → awaiting-items
//                                  ↑ (auto-advance after each line)
//                          → wave-complete
//   Tote swap: picker can scan a different tote at any time during
//   awaiting-location / awaiting-items — BE closes the previous tote
//   and opens the new one; phase doesn't change.
//
// HiddenScanInput is camera-disabled (HID-only, matches Dispatch). It
// must be a direct child of `scan-panel` per the CLAUDE.md rule.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, AlertTriangle, Pause, PackageOpen, PackageCheck, WifiOff } from "lucide-react";
import ScanPromptHero from "../picking-shared/ScanPromptHero";
import { showToast } from "../helpers/showToast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import HiddenScanInput from "../HiddenScanInput";
import ScanStatusIcons from "../ScanStatusIcons";
import ScanFlashOverlay from "../ScanFlashOverlay";
import { playSuccess, playError, playBoxAdd, playToteFinalised } from "../helpers/audioPlayer";
import { usePickingIdleTimer } from "../../hooks/usePickingIdleTimer";
import { useDemoVisible } from "../helpers/demoMode";
import MissingItemCountList from "./MissingItemCountList";
import MissingLocationGate from "./MissingLocationGate";
import MissingFinishConfirm, { type UncountedLine } from "./MissingFinishConfirm";
import MissingResolutionCard, { type MissingResolution } from "./MissingResolutionCard";
import type {
    ActiveWaveLine,
    ActiveWaveHeader,
    ActiveWaveProgress,
} from "../../hooks/useActiveWave";
import { usePickingSession, groupTotalsFor, othersAtLocationFor, locationTotalsFor, orderScopeFor, type PartPickedOrder, type PartPickedLine } from "../../hooks/usePickingSession";
import PartPickedSwapCard from "./PartPickedSwapCard";
import PickingScreen, { type LastPickedItem } from "../picking-shared/PickingScreen";
import LargeAnchorScan from "./LargeAnchorScan";
import YorkFillPrompt, { type YorkQuarters } from "./YorkFillPrompt";
import YorkDropOff, { type YorkDropReason } from "./YorkDropOff";
import { openYorkDrop, confirmYorkDrop } from "./recordYorkDrop";
import { afterYorkAnswer, afterYorkDrop } from "./yorkFlow";
import { startLargeWaveAt } from "./startLargeWave";
import ManualPickModal from "../picking-shared/ManualPickModal";
import CloseWaveModal, { type CloseWaveStep, type PartialOrder } from "./CloseWaveModal";
import PickingTopBar from "../picking-shared/PickingTopBar";
import { pickTypeKindFor, pickTypeLabelKeyFor } from "./pickTypeLabel";
import { RollCageCard } from "../picking-shared/RollCageEstimate";
import { estimateRollCages, linesForOrder } from "../picking-shared/rollCages";
import { aisleUnitsFor } from "../picking-shared/aisleUnits";
import { usePickSpeed } from "../picking-shared/usePickSpeed";
import { useCycleScore, type PointsTargetKey } from "../gamification";

type Phase =
    | "awaiting-resume-tote"       // resumed wave: re-scan the pause-time tote first
    | "awaiting-starting-location" // Large only: anchor picker before any other scan
    | "awaiting-first-tote"        // open tote required before picking
    | "awaiting-location"
    | "awaiting-items"
    | "missing-location"
    | "missing-counting"
    | "missing-resolved"          // outcome screen: what happened + what to do
    | "order-complete"             // Large only: transient tick between orders, then Scan Tote
    | "wave-complete"
    | "york-fill"                  // Large only: how full is your york? (before the location scan)
    | "york-drop"                  // Large only: take the york to the drop zone
    | "next-order";                // Large only: order done — scan a location to open the next
type ScanStatus = "idle" | "success" | "error" | "complete";

interface Props {
    wave: ActiveWaveHeader;
    initialCurrentLine: ActiveWaveLine | null;
    initialLookahead: ActiveWaveLine[];
    initialProgress: ActiveWaveProgress;
    onWaveComplete: () => void;
    // Local-first bootstrap inputs — populated only when
    // wave.picking_local_first_enabled. Allows the hook to skip the
    // initial /picking/active round-trip and hydrate from the same
    // payload the parent already loaded.
    initialLines?: ActiveWaveLine[];
    initialBarcodeMap?: Record<string, string[]>;
}

interface ScanResponse {
    ok: boolean;
    reason?: string;
    expected?: string;
    // wrong_warehouse only: which warehouse the scanned location
    // actually belongs to.
    locationWarehouse?: string;
    currentLine?: ActiveWaveLine | null;
    // Back-compat: a single next-line. Newer responses also include
    // `lookahead` (an array). When both are present, lookahead is
    // authoritative.
    nextLine?: ActiveWaveLine | null;
    // FE queue of the upcoming pending/picking lines AFTER the current
    // one. Length up to settings.picking_lookahead_orders. Drives the
    // multi-line lookaheadQueue so the hero can swap instantly on each
    // line completion (matches Shelf/Forecast Drop behaviour).
    lookahead?: ActiveWaveLine[];
    progress?: ActiveWaveProgress | null;
    // Monotonic version of the wave. Bumps whenever the line set
    // changes structurally (JIT order append, missing-item re-slot).
    // The FE refreshes its lookahead from each response anyway, so the
    // version is currently informational — kept for diagnostics + any
    // future cross-tab invalidation work.
    waveVersion?: number | null;
    lineItemsComplete?: boolean;
    waveComplete?: boolean;
    awaitingFinalTote?: boolean;
    // Large only: the scanned item finished an order and the BE closed
    // its tote — the picker must scan a fresh tote for the next order.
    awaitingTote?: boolean;
    advancedLineId?: number | null;
    // Tote-registration response — set when the scanned barcode was
    // recognised as a tote.
    toteRegistered?: boolean;
    toteSwapped?: boolean;
    currentToteId?: string;
    totalTotes?: number;
    // order_part_picked only (Singles tote swap mid-order) — see
    // PartPickedOrder in usePickingSession.
    orderId?: string;
    toteId?: string;
    qtyPicked?: number;
    qtyRequired?: number;
    remaining?: PartPickedLine[];
    // Single-wave only: number of brand-new orders the BE pulled into
    // this wave on the back of this scan. Drives a subtle toast so
    // the picker knows their wave just grew.
    newOrdersAdded?: number;
    // Identified-tote-conflict fields. When `tote_in_use_elsewhere`
    // is the rejection reason, the BE returns who's holding the tote
    // so the FE can render "Held by Sarah on wave #142" instead of a
    // generic "in use elsewhere" toast.
    heldByModule?: "picking" | "drop";
    heldByWaveId?: number | null;
    heldByPickerUid?: number | null;
    heldByPickerName?: string | null;
    heldByWaveStatus?: string | null;
    // scan-item only: identity of the wave line this scan actually
    // landed on — the hero line OR a same-location sibling
    // (scan-any-item-at-location). Drives the green "last picked"
    // banner; optional so older BE bundles keep working.
    pickedLine?: {
        line_id: number;
        sku: string;
        product_title: string | null;
        product_barcode: string | null;
        product_thumbnail_url: string | null;
        product_image_url: string | null;
    };
}

// Map a wave line (or the BE's pickedLine echo — same product_* field
// names) onto what the green "last picked" banner renders.
const toLastPicked = (line: {
    sku: string;
    product_title?: string | null;
    product_barcode?: string | null;
    product_image_url?: string | null;
    product_thumbnail_url?: string | null;
}): LastPickedItem => ({
    sku: line.sku,
    title: line.product_title ?? null,
    barcode: line.product_barcode ?? null,
    imageUrl: line.product_image_url ?? null,
    thumbnailUrl: line.product_thumbnail_url ?? null,
});

// Legacy-path line mirror (local-first OFF): fold a scan response's
// current line + lookahead back into the stale initial line set so the
// aisle-units card keeps moving. Lines in the response replace their
// stale copies by line_id. When the hero has MOVED ON (current line_id
// changed), the line it left is closed out — the BE only advances past
// a line once it is picked (or written off by the missing flow, which
// re-syncs separately) — so its units stop counting as owed.
export const mergeLegacyLines = (
    prev: ActiveWaveLine[],
    fresh: ActiveWaveLine[],
    prevCurrent: ActiveWaveLine | null,
    nextCurrent: ActiveWaveLine | null,
): ActiveWaveLine[] => {
    const byId = new Map(fresh.map(l => [l.line_id, l]));
    const leftLineId = prevCurrent && prevCurrent.line_id !== nextCurrent?.line_id
        ? prevCurrent.line_id
        : null;
    const seen = new Set<number>();
    const merged = prev.map(l => {
        seen.add(l.line_id);
        const f = byId.get(l.line_id);
        if (f) return { ...l, ...f };
        if (l.line_id === leftLineId && (l.status === "pending" || l.status === "picking")) {
            return { ...l, qty_picked: l.qty_required, status: "picked" as const };
        }
        return l;
    });
    // A line the snapshot never held (Large JIT append) joins the set.
    for (const f of fresh) if (!seen.has(f.line_id)) merged.push(f);
    return merged;
};

// Shape returned by /picking/missing/:id/start
interface ExpectedSku {
    line_id: number;
    sku: string;
    qty_required: number;
    qty_picked: number;
    status: string;
}
// One row of the real stock check opened for this count — same shape the
// Stock Check module renders, so the count screen mirrors it.
export interface StockCheckLine {
    sku: string;
    title: string;
    barcode?: string | null;
    expected_qty: number;
    scanned_qty: number;
    /**
     * How much of expected_qty is reserved for an order. Reserved units are on
     * the shelf and are counted like anything else, but leaving one unscanned
     * is never evidence of loss — it may already be in a tote. Excluded from
     * the "remove these from the location?" figure for that reason.
     */
    allocated_qty?: number;
}
// An item found during the count that is on the picker's OWN pick list.
interface PickRelevant {
    sku: string;
    line_id: number;
    location: string;
    qty_outstanding: number;
}
interface MissingStartResponse {
    ok: boolean;
    line: {
        line_id: number; sku: string; location: string; qty_required: number; qty_picked: number;
        /**
         * Outstanding units across EVERY open line for this SKU at this
         * location — the hero's counter. The builder writes one line per
         * inventory batch, so "4 missing" on the hero can be four qty-1
         * lines; /finish resolves them together, so this is what the count
         * is looking for. Absent on an older BE, which resolved one line.
         */
        group_qty_outstanding?: number;
        group_line_ids?: number[];
    };
    expected: ExpectedSku[];
    // Real stock check (origin='picking_missing'). Null when the BE couldn't
    // open one — the count still works, it just isn't recorded as a check.
    stock_check_id?: number | null;
    location_display_name?: string | null;
    stock_check_lines?: StockCheckLine[];
    resumed_stock_check?: boolean;
    took_over_from?: string | null;
}
interface MissingScanResponse {
    ok: boolean;
    reason?: string;
    sku?: string;
    counts?: Record<string, number>;
    expected?: string;       // location, when scan-location rejects
    stock_check_lines?: StockCheckLine[] | null;
    /**
     * How the shared stock-check classifier read the scan. `known_not_here`
     * means the SKU is a real product but isn't stocked at this location —
     * surplus. The count NEVER books that in on its own (doing so silently
     * invented inventory, at £0.00, before 2026-08-16); the picker is asked,
     * and only an explicit yes routes through the Stock Check module's Add.
     */
    stock_check_outcome?: string | null;
    pick_relevant?: PickRelevant | null;
}
// A surplus scan awaiting the picker's decision. `extraUnit` distinguishes a
// further unit of a SKU the count already knows about (surplus /
// already_added) from a SKU not recorded here at all — the wording differs.
interface SurplusPrompt {
    sku: string;
    barcode: string;
    extraUnit: boolean;
}
// What the BE's resolution says, in enough detail for the outcome screen to
// state the next action without a second round-trip.
interface MissingFinishSummary {
    sku: string;
    location: string;
    requiredQty: number;
    foundQty: number;
    /**
     * Units the count found that stay here on lines the picker now picks
     * before moving on (a partial find). 0 when nothing stays.
     */
    keptHereQty?: number;
    redirectLocation?: string | null;
    /** Human-readable form of redirectLocation (aisle-bay-level / name). */
    redirectLocationDisplay?: string | null;
    redirectIsNextStop?: boolean;
    flaggedOrders?: number;
    flaggedQty?: number;
    ordersFlaggedForReview?: number;
}
interface MissingFinishResponse {
    ok: boolean;
    resolution: "found-here" | "found-elsewhere" | "not-found";
    redirectLocation?: string | null;
    redirectLocationDisplay?: string | null;
    newLineId?: number | null;
    waveComplete?: boolean;
    summary?: MissingFinishSummary;
    currentLine?: ActiveWaveLine | null;
    // Back-compat alias for `lookahead[0]` — kept so older BE bundles
    // still hydrate the queue. Newer BE responses send `lookahead`.
    nextLine?: ActiveWaveLine | null;
    lookahead?: ActiveWaveLine[];
    progress?: ActiveWaveProgress | null;
    waveVersion?: number | null;
}

// Holds the picker's current missing-item session state. Lives on the
// parent component because the single HiddenScanInput dispatches into
// these endpoints, and the count list reads from `counts` here.
interface MissingState {
    line: {
        line_id: number; sku: string; location: string;
        qty_required: number; qty_picked: number;
        group_qty_outstanding?: number;
        group_line_ids?: number[];
    };
    expected: ExpectedSku[];
    counts: Record<string, number>;
    // The real stock check backing this count (null when none was opened).
    stockCheckId: number | null;
    locationDisplayName: string;
    // Full-location line list, mirrored from the Stock Check module.
    stockCheckLines: StockCheckLine[];
    // Items found during the count that are on the picker's own pick list.
    // Accumulated (not replaced) so the picker still sees everything they
    // turned up when they get back to the pick screen.
    pickRelevant: PickRelevant[];
}

// Initial phase. Order of gates:
//   1. Large waves wait for a starting-location scan before anything
//      else — the BE returns a wave with no lines until that scan.
//      (Single is upfront-allocated and skips this.)
//   2. With no open tote → first-tote phase.
//   3. If the warehouse has require_location_scan turned off, skip
//      the per-line location scan entirely (applies to all pick
//      types). The BE auto-transitions the line on scanItem so the
//      picker can move straight from item → item.
//   4. Otherwise normal pick flow with location scan.
const initialPhaseFor = (
    line: ActiveWaveLine | null,
    hasOpenTote: boolean,
    needsStartingLocation: boolean,
    requireLocationScan: boolean,
    // Resume-tote verification gate — outranks everything: a resumed
    // wave accepts nothing until its pause-time tote is re-scanned.
    pendingResumeTote: string | null = null,
): Phase => {
    if (pendingResumeTote) return "awaiting-resume-tote";
    if (needsStartingLocation) return "awaiting-starting-location";
    if (!line) return "wave-complete";
    if (!hasOpenTote) return "awaiting-first-tote";
    if (line.status === "picking") return "awaiting-items";
    if (!requireLocationScan) return "awaiting-items";
    return "awaiting-location";
};

const ActiveWaveScreen = ({
    wave, initialCurrentLine, initialLookahead, initialProgress, onWaveComplete,
    initialLines, initialBarcodeMap,
}: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [currentLine, setCurrentLine] = useState<ActiveWaveLine | null>(initialCurrentLine);
    // Multi-line lookahead queue, sized by settings.picking_lookahead_orders.
    // Receives the line that becomes current once the current one is
    // fully picked, plus the next N after that. On a line-completing
    // scan the FE shifts the head off and uses it instantly — no
    // round-trip wait, matching Shelf/Forecast Drop's `lookaheadQueue`.
    // The BE response (`applyResponse` below) overwrites the whole
    // queue on every scan, so JIT order appends + missing-item re-slots
    // stay consistent without FE bookkeeping.
    const [lookaheadQueue, setLookaheadQueue] = useState<ActiveWaveLine[]>(initialLookahead);
    // Legacy (local-first OFF) mirror of the wave's line set for the
    // "Units left by aisle" card. Under local-first the session hook
    // owns the live lines; with it off the hook sits idle on the initial
    // snapshot and scan responses carry only the current line + lookahead,
    // so this copy is kept fresh from those (see applyResponse). Unused
    // when local-first is on.
    const [legacyLines, setLegacyLines] = useState<ActiveWaveLine[]>(() => initialLines ?? []);
    const [progress, setProgress] = useState<ActiveWaveProgress>(initialProgress);
    const [currentToteId, setCurrentToteId] = useState<string | null>(wave.current_tote_id);
    // Large anchors the picker with a starting-location scan before any
    // pick. Single (light) is upfront-allocated (starting_location stays
    // null), so it must NOT show the no-op starting-location phase.
    const needsStartingLocation =
        wave.pick_type === "large" && wave.starting_location === null;
    // Resume-tote verification — the BE sets pending_resume_tote on
    // resume when a tote was open at pause; the picker must re-scan
    // that exact tote before anything else registers. Cleared when the
    // matching tote scan is accepted.
    const [pendingResumeTote, setPendingResumeTote] = useState<string | null>(
        wave.pending_resume_tote ?? null);
    const [phase, setPhase] = useState<Phase>(() =>
        initialPhaseFor(initialCurrentLine, !!wave.current_tote_id, needsStartingLocation,
            wave.require_location_scan, wave.pending_resume_tote ?? null));
    const [scanValue, setScanValue] = useState("");
    const [scanStatus, setScanStatus] = useState<ScanStatus>("idle");
    const [submitting, setSubmitting] = useState(false);
    // Large only: how full the picker last said their york was, and why
    // they are being sent to the drop zone. Deliberately NOT persisted —
    // this screen is keyed by wave_id, so a reload lands back on the
    // fill prompt and the picker simply answers again. Re-asking is
    // cheap; a remembered-but-wrong fill level overfills a york at the
    // far end of the warehouse.
    const [yorkQuarters, setYorkQuarters] = useState<YorkQuarters>(0);
    const [yorkDropReason, setYorkDropReason] = useState<YorkDropReason>("full");
    // The open york_drops row, so the confirm lands on the same one. A
    // ref, not state: nothing renders from it, and a re-render between
    // the open and the confirm must not lose it.
    const yorkDropIdRef = useRef<number | null>(null);
    // The unit the picker most recently put in the tote — drives the
    // green banner at the bottom of PickingScreen. Often NOT the hero
    // line (scan-any-item-at-location), so it's set from whichever
    // line the scan actually landed on: session.lines on the
    // local-first path, body.pickedLine on the server path. Null until
    // the first pick of the session.
    const [lastPicked, setLastPicked] = useState<LastPickedItem | null>(null);
    const [missing, setMissing] = useState<MissingState | null>(null);
    // "Done counting" pressed — the confirm is up (mirrors the Stock Check
    // module's complete confirm, including the remove-or-keep choice).
    const [finishConfirm, setFinishConfirm] = useState(false);
    // Resolved count awaiting the picker's acknowledgement. Non-null exactly
    // while phase === "missing-resolved".
    const [resolved, setResolved] = useState<MissingResolution | null>(null);
    // Singles: a tote swap the BE refused because the current order is
    // part-picked (order_part_picked). Non-null while the "finish this
    // order first?" card is up; `barcode` is the york the picker tried
    // to swap to, re-posted with force on "Swap anyway".
    const [swapGuard, setSwapGuard] = useState<{ barcode: string; order: PartPickedOrder } | null>(null);
    const swapGuardRef = useRef<{ barcode: string; order: PartPickedOrder } | null>(null);
    useEffect(() => { swapGuardRef.current = swapGuard; }, [swapGuard]);
    const [swapForcing, setSwapForcing] = useState(false);
    // Surplus scanned during a count, awaiting "add it?" — see SurplusPrompt.
    const [surplusPrompt, setSurplusPrompt] = useState<SurplusPrompt | null>(null);
    const [addingSurplus, setAddingSurplus] = useState(false);
    const [pausing, setPausing] = useState(false);
    // Singles only: the Pause tap opens a Pause / Close-wave choice
    // (CloseWaveModal). null = closed. `closing` covers the preview and
    // the commit round-trips.
    const [closeModal, setCloseModal] = useState<CloseWaveStep | null>(null);
    const [closing, setClosing] = useState(false);
    const demo = useDemoVisible();
    // Client-side pick-rate counter — fed by every accepted item scan
    // and rendered in the PickingScreen's action bar bottom-right.
    // Resets implicitly when the parent unmounts (wave complete /
    // navigate away), which is the intended scope.
    const { rate: pickSpeed, recordPick } = usePickSpeed();

    // Trophy points. The cycle is scan-to-scan — the walk to the next
    // location is part of the pick being measured — so the first pick of a
    // wave opens the window and the second is the first one scored.
    //
    // Target per pick type: a Large pick and a Single are different jobs.
    // The pick type is stored as 'light' and shown to humans as "Single".
    const pointsTargetKey: PointsTargetKey =
        wave.pick_type === "large" ? "picking_timer_large_ms"
        : wave.pick_type === "pick_and_sort" ? "picking_timer_pick_and_sort_ms"
        : "picking_timer_light_ms";
    const cycle = useCycleScore(wave.warehouse, pointsTargetKey, `picking-best-time-${wave.pick_type}`);
    const cycleRef = useRef(cycle);
    cycleRef.current = cycle;

    // Local-first session — owns scan state locally when the
    // warehouse has the feature flag on. The hook always mounts
    // (React rules), but its processScan is only called when the
    // flag is enabled. With the flag off, the hook just sits idle
    // holding its initial state.
    const localFirst = !!wave.picking_local_first_enabled;
    // Pick & Sort is picked into york cages only, so every "scan a tote
    // first" nudge on a P&S wave names the york — the same word as the
    // SCAN YORK hero, or the picker goes looking for a box. Pembroke
    // Singles are picked into yorks too, so they get the same word.
    const picksIntoYork = wave.pick_type === "pick_and_sort"
        || (wave.pick_type === "light" && wave.warehouse === "Pembroke");
    const scanToteFirstKey = picksIntoYork
        ? "picking_scan_york_first"
        : "picking_scan_tote_first";
    const session = usePickingSession({
        wave,
        lines: initialLines ?? [],
        currentLine: initialCurrentLine,
        nextLine: initialLookahead[0] ?? null,
        lookahead: initialLookahead,
        progress: initialProgress,
        waveVersion: wave.wave_version,
        barcodeMap: initialBarcodeMap ?? {},
    }, {
        // The local-first path flashes green optimistically. When the
        // BE later rejects (e.g. starting_location was already set on
        // this wave + no open tote → scan_tote_first), the picker
        // would otherwise see no feedback — reconciliation silently
        // rewinds state. Surface a toast + error cue here.
        onSyncReject: (reason, kind, barcode, extras) => {
            // No eligible order left (e.g. a Large pool drained before the
            // first tote scan) — the BE auto-released the wave. This isn't
            // a scan error: show a neutral "no orders" notice and bounce
            // back to the Pick Queue (onWaveComplete refetches → the
            // selector renders since the wave is gone).
            if (reason === "wave_empty_auto_released") {
                showToast("info", t("picking_no_orders_to_pick"), { toastId: "picking-no-orders" });
                onWaveComplete();
                navigate("/picking", { replace: true });
                return;
            }
            // Singles tote swap mid-order — not a bad scan, a question.
            // No error cue: the card IS the feedback, and it carries the
            // picker's two answers (PartPickedSwapCard).
            if (reason === "order_part_picked" && extras?.partPicked) {
                setSwapGuard({ barcode, order: extras.partPicked });
                return;
            }
            playError();
            flash("error");
            // A locally-accepted item pick the BE later rejected — the
            // green banner may be showing exactly that pick. Blank it
            // rather than keep asserting a unit that never registered
            // (recovering the true previous pick isn't worth a refetch).
            if (kind === "scan-item") setLastPicked(null);
            // Resume-tote gate — show WHICH tote unlocks the wave.
            if (reason === "scan_resume_tote_first") {
                showToast("error",
                    t("picking_scan_resume_tote_first", { tote: pendingResumeTote ?? "" }),
                    { toastId: "picking-resume-tote" });
                return;
            }
            // Starting-location barcode that lives in ANOTHER warehouse
            // — name it (needs interpolation, so it can't go through
            // the plain key map below).
            if (reason === "wrong_warehouse") {
                showToast("error",
                    t("scan_wrong_warehouse", { warehouse: extras?.locationWarehouse ?? "?" }),
                    { toastId: "picking-wrong-warehouse" });
                return;
            }
            // Every BE rejection gets a toast — unmapped reasons fall
            // back to the generic "Scan rejected" so a new BE reason
            // never regresses into a silent X.
            const reasonKey =
                reason === "wrong_location"               ? "picking_wrong_location"             :
                reason === "wrong_item"                   ? "picking_wrong_item"                 :
                reason === "scan_location_first"          ? "picking_scan_location_first"        :
                reason === "scan_tote_first"              ? scanToteFirstKey            :
                reason === "scan_starting_location_first" ? "picking_scan_starting_location_first" :
                reason === "invalid_starting_location"    ? "picking_invalid_starting_location"   :
                reason === "tote_unavailable"             ? "picking_tote_unavailable"           :
                reason === "tote_in_use_elsewhere"        ? "picking_tote_in_use_elsewhere"      :
                reason === "single_tote_order"            ? "picking_single_tote_order"          :
                reason === "tote_not_york"                ? "picking_tote_not_york"              :
                // A scan that wasn't the expected location/item got routed
                // to the BE as a tote candidate and came back not-a-tote —
                // i.e. an unrecognised barcode.
                reason === "unknown_tote"                 ? "picking_unknown_barcode"            :
                reason === "line_already_complete"        ? "picking_wrong_item"                 :
                "picking_scan_rejected";
            showToast("error", t(reasonKey), { toastId: `picking-sync-${reason}` });
        },
        // BE confirmed a tote / starting-location scan whose cue we held
        // back. Play the success cue now — this is the first positive
        // feedback the picker gets for that scan, so it can never be
        // contradicted by a later rejection.
        onSyncAccept: () => {
            playBoxAdd();
            flash("success");
        },
        // Authoritative wave-complete signal from the BE SSE pipeline.
        // For Single/Large local-first this is the ONLY trigger (the
        // hook deliberately suppresses local waveComplete because
        // JIT-append might still happen on the BE). For Pick & Sort
        // local-first the inline scan handler usually fires first; the
        // ref-guarded sceduler below dedupes either way.
        onWaveCompleted: () => {
            scheduleWaveCompleteNavigate();
        },
        // Reconcile found no active wave — it was paused (idle cron),
        // force-released, or completed elsewhere. Leave the scan screen
        // instead of letting the picker scan into silent 403s; their
        // unsynced picks stay on the device and replay after resume.
        onWaveGone: () => {
            // The completion path also empties /picking/active — don't
            // misreport a finished wave as interrupted.
            if (waveCompleteHandledRef.current) return;
            showToast("info", t("picking_wave_interrupted"), { toastId: "picking-wave-gone" });
            onWaveComplete();
            navigate("/picking", { replace: true });
        },
    });

    // Offline / unsynced-scans awareness for the banner below. The
    // sync queue itself listens to 'online' to flush — this state only
    // drives what the picker SEES.
    const [offline, setOffline] = useState<boolean>(
        typeof navigator !== "undefined" && navigator.onLine === false);
    useEffect(() => {
        const onOnline = () => setOffline(false);
        const onOffline = () => setOffline(true);
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        return () => {
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);
        };
    }, []);
    // One error cue the moment syncing first stalls — the local-first
    // path beeps green on every accepted scan, so without this the
    // picker has no idea their picks stopped landing (wave 5734,
    // 2026-08-19: 13 minutes of green beeps in a wifi blackspot). The
    // persistent banner carries the detail; the cue just makes them
    // look up.
    const prevSyncErrorRef = useRef(false);
    useEffect(() => {
        if (session.syncError && !prevSyncErrorRef.current) playError();
        prevSyncErrorRef.current = session.syncError;
    }, [session.syncError]);
    const showSyncBanner = localFirst && (offline || session.syncError);

    // Single source of truth for "wave finished → celebrate + bounce
    // to picker selector". Both the legacy scan-handler completion
    // branches and the local-first SSE callback funnel through here.
    // Ref-guarded so two concurrent triggers can't double-schedule the
    // navigate timer (which would force-pop the picker out of any
    // newly-built wave they just claimed).
    // Wave-complete is a two-beat sequence: the amber tick (the "you
    // just finished" cue), then a "Wave complete" message + icon, then
    // bounce back to the picker selector. `waveCompleteStage` tracks
    // which beat is showing.
    const [waveCompleteStage, setWaveCompleteStage] = useState<"tick" | "message">("tick");
    const waveCompleteHandledRef = useRef(false);
    // A missing-item resolution card is on screen. Kept as a ref because the
    // scheduler below is a stable callback the SSE listener holds onto.
    const resolutionOpenRef = useRef(false);
    // Set when a wave completion arrived while that card was up.
    const waveCompleteDeferredRef = useRef(false);
    const scheduleWaveCompleteNavigate = useCallback(() => {
        // The last thing a picker did was resolve a missing item, and the
        // resolution card is telling them what happens to it. A not-found
        // resolution can be what finishes the wave, so this fires the instant
        // they read it — and without the defer, the card is replaced by the
        // amber tick and the picker is bounced to the selector never having
        // seen that the item was flagged for dispatch. Hold the completion;
        // continueFromResolution releases it.
        if (resolutionOpenRef.current) {
            waveCompleteDeferredRef.current = true;
            return;
        }
        if (waveCompleteHandledRef.current) return;
        waveCompleteHandledRef.current = true;
        playToteFinalised();
        // The wave-complete tick is rendered by the "wave-complete" phase
        // (hardcoded status="complete" during the "tick" beat), so DON'T
        // flash(): a lingering scanStatus="complete" would bleed the tick
        // onto the "Wave complete" message beat. Clear it instead.
        setScanStatus("idle");
        setWaveCompleteStage("tick");
        cycleRef.current.end();
        setPhase("wave-complete");
        // Beat 1: amber tick (500ms). Beat 2: "Wave complete" message +
        // icon. Then: Large waves are one-order-per-wave, so completion
        // here means the ORDER is done — hand the picker the next-order
        // anchor prompt (scan wherever you now stand to open another, or
        // Done to stop) instead of bouncing to the selector. Other pick
        // types return to the pick list as before.
        window.setTimeout(() => setWaveCompleteStage("message"), 500);
        window.setTimeout(() => {
            // Pick to tote (queue 4): one order, one tote, no york — the
            // tote is now in the Put Away queue, so return the picker to
            // the Pick to tote list for their next order.
            if (wave.pick_queue === 4) {
                onWaveComplete();
                navigate("/pick-face/pick-to-tote", { replace: true });
                return;
            }
            if (wave.pick_type === "large") {
                // The york prompt sits between finishing an order and
                // scanning the next location. It is skipped for the
                // FIRST order of a session — the picker has just
                // fetched a york, so the answer would always be
                // "empty" — and this transition only ever fires AFTER
                // an order, so by definition it is never the first.
                setPhase("york-fill");
                return;
            }
            onWaveComplete();
            navigate("/picking", { replace: true });
        }, 1800);
    // flash is stable after first render; navigate is from RR and stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onWaveComplete, navigate]);

    // Stranded-wave recovery. Reaching the "wave-complete" phase without
    // scheduleWaveCompleteNavigate having run means the BE still thinks
    // this wave is active but it has nothing left to pick — e.g. the BE
    // crashed/restarted between the final pick (which had already
    // committed) and the wave-completion transaction, and we then
    // mounted straight into currentLine=null → "wave-complete". The
    // tick screen has no buttons and scans are disabled, so without
    // this the picker is permanently stuck on the amber tick.
    //
    // Grace period first: in normal local-first completions the
    // authoritative wave.completed SSE (or the scan response) flips
    // waveCompleteHandledRef within well under a second — the timer
    // is cleaned up / the ref check bails and this never fires. If the
    // stranded state persists, POST /pause: the BE's canonical recovery
    // — completeWaveIfReady finishes a fully-picked wave (closing its
    // tote, dispatch-ready); a genuinely mid-pick wave just pauses for
    // a clean resume. Either way, bounce to the selector.
    useEffect(() => {
        if (phase !== "wave-complete" || waveCompleteHandledRef.current) return;
        const timer = window.setTimeout(async () => {
            if (waveCompleteHandledRef.current) return;
            waveCompleteHandledRef.current = true;
            try {
                await apiFetch(`/picking/wave/${wave.wave_id}/pause`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({}),
                });
            } catch { /* network — bounce anyway; the refetch resolves it */ }
            onWaveComplete();
            navigate("/picking", { replace: true });
        }, 2500);
        return () => window.clearTimeout(timer);
    // navigate + onWaveComplete are stable; wave_id never changes mid-mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    // Large: an order just finished and the wave continues into the next
    // JIT order. The BE closed the filled tote; the picker must scan a
    // fresh one. Show the order-complete tick for 500ms, then flip to
    // the Scan Tote prompt. The ref guards against double-firing (the
    // optimistic + BE-confirmed paths, or repeat SSE) and tells the
    // local-first mirror effect not to clobber the transient phase.
    const orderCompleteRef = useRef(false);
    const beginOrderCompleteTote = useCallback(() => {
        if (orderCompleteRef.current) return;
        orderCompleteRef.current = true;
        playSuccess();
        // The order-complete tick is rendered by the "order-complete"
        // phase itself (hardcoded status="complete"), so DON'T flash():
        // flash sets scanStatus="complete" for 900ms which would linger
        // and re-show the tick on the SCAN TOTE (awaiting-first-tote)
        // hero after the 500ms transition. Clear it instead so the Scan
        // Tote prompt is clean.
        setScanStatus("idle");
        setCurrentToteId(null);
        setPhase("order-complete");
        window.setTimeout(() => {
            orderCompleteRef.current = false;
            // In local-first the hook fires orderComplete for EVERY
            // finished order — it can't know the BE has no next order to
            // append. If this was actually the LAST order, the BE
            // completes the wave and the `wave.completed` SSE has fired
            // scheduleWaveCompleteNavigate by now (waveCompleteHandledRef
            // set). In that case don't prompt for a tote — let the
            // wave-complete message stand.
            if (waveCompleteHandledRef.current) return;
            setPhase("awaiting-first-tote");
        }, 500);
    // flash is stable after first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mirror the hook's authoritative state into the existing
    // component state when local-first is on. The render code stays
    // unchanged — it reads from currentLine / currentToteId /
    // progress / phase as before.
    //
    // Deps MUST be primitives, not the `session.*` objects. The hook
    // recomputes `progress` (and constructs the `session` wrapper)
    // every render, so an object-keyed dep array sees "changed" on
    // every render → effect fires → setState → re-render → infinite
    // loop ("Maximum update depth exceeded"). Drilling down to the
    // line_id / qty_picked / status / counts gives React real values
    // to compare and lets the effect run only when something
    // meaningful changes.
    const sessionCurrentLineId = session.currentLine?.line_id ?? null;
    const sessionCurrentQty    = session.currentLine?.qty_picked ?? null;
    const sessionCurrentStatus = session.currentLine?.status ?? null;
    const sessionToteId        = session.currentToteId;
    const sessionItemsPicked   = session.progress.itemsPicked;
    const sessionTotalItems    = session.progress.totalItems;
    // Large anchor window detector for the phase gate below: zero
    // lines = the wave has never held a line (JIT append pending).
    const sessionLinesCount    = session.lines.length;
    // Read starting_location from the hook's authoritative wave (which
    // reconcile updates after the BE confirms a starting-location scan),
    // not from the parent's `wave` prop — the parent never re-fetches
    // mid-wave, so its starting_location stays null forever. Without
    // this, needsStart never flips false: phase is pinned to
    // awaiting-starting-location, but validateScan moves on (because
    // waveRef.current.starting_location IS set) and rejects the next
    // scan as scan_tote_first — while the demo panel still only offers
    // starting-location buttons. Affects Large (the only pick type that
    // anchors on a starting-location scan).
    const sessionStartingLocation = session.wave.starting_location;
    const sessionPendingResumeTote = session.wave.pending_resume_tote ?? null;
    // A missing-item count is on screen (gate, count list, or the outcome
    // card). The mirror effect below must not touch phase while any of them
    // are up — see the guard inside it. In the dep array so the effect
    // re-runs (and re-derives phase from the session) the moment the flow
    // ends.
    const missingActive = missing !== null || resolved !== null;
    useEffect(() => {
        if (!localFirst) return;
        // The picker is mid-count. Any session change that lands now — a
        // sync ack, a reconcile, an SSE append — must NOT re-derive phase:
        // doing so tore the count screen down under them mid-flow, dropping
        // them back on the pick hero with the location gate never scanned and
        // no way to finish the count they had started. (Reported as "the flag
        // process never asked me to scan the location".) Their own /finish
        // reconciles and re-derives phase when the flow resolves.
        if (missingActive) return;
        // Recompute the combined group quantities AND the "Also at this
        // location" list from the LIVE line set — the BE-baked fields go
        // stale once a sibling is picked locally: the group counter would
        // reset at the batch boundary (see groupTotalsFor) and a just-
        // picked same-location item would keep showing on the also-card
        // until the next server refetch (see othersAtLocationFor).
        setCurrentLine(session.currentLine
            ? {
                ...session.currentLine,
                ...groupTotalsFor(session.lines, session.currentLine),
                ...locationTotalsFor(session.lines, session.currentLine),
                other_lines_at_location: othersAtLocationFor(session.lines, session.currentLine),
            }
            : null);
        setProgress(session.progress);
        setCurrentToteId(session.currentToteId);
        // Keep the component's copy in sync for the resume hero +
        // toast (the hook clears it optimistically on the matching
        // tote scan; reconcile re-reads it from the BE).
        setPendingResumeTote(sessionPendingResumeTote);
        // An order-complete → Scan Tote transition is mid-flight (Large):
        // the 500ms tick timer owns the phase until it flips to
        // awaiting-first-tote. Keep syncing the data above (so the next
        // order's line is ready once it arrives) but don't let the phase
        // gate below clobber the transient "order-complete" tick.
        if (orderCompleteRef.current) return;
        // Phase derives from the same gates as initialPhaseFor. Large
        // only — Single (light) is upfront-allocated, no anchor scan.
        const needsStart = wave.pick_type === "large"
            && sessionStartingLocation === null;
        const nextPhase = initialPhaseFor(
            session.currentLine,
            !!session.currentToteId,
            needsStart,
            wave.require_location_scan,
            sessionPendingResumeTote,
        );
        // Large: between orders there's a brief window where the local
        // line set is fully picked (session.currentLine === null) but
        // the BE is still JIT-appending the NEXT order — the refetch
        // that brings it hasn't landed yet. initialPhaseFor maps a null
        // line to "wave-complete", which would flash the picking-
        // complete screen for that window before the next order swaps
        // in. Suppress it: the wave is only truly done when the BE
        // publishes wave.completed (onWaveCompleted → scheduleWave-
        // CompleteNavigate sets waveCompleteHandledRef). Until then,
        // hold the current phase so the picker keeps seeing the
        // just-finished hero, not a false "complete" tick. Upfront-
        // allocated types (Pick & Sort, Single) genuinely finish when
        // their lines run out, so they're unaffected.
        // Paired wave (Alex, 2026-10-16): the local line set is only this
        // picker's half. When it runs out the BE either completes the
        // half (wave.completed SSE / the scan response's wave_complete →
        // scheduleWaveCompleteNavigate) or folds the other half onto the
        // end of this wave and pushes its lines down — so hold the
        // current phase here exactly as for a Large JIT gap, never show
        // a false "complete" tick, and never fire the stranded-wave pause.
        if (nextPhase === "wave-complete"
            && (wave.pick_type === "large" || (wave.segment_count ?? 1) > 1)
            && !waveCompleteHandledRef.current) {
            // Anchor window: the wave has never held a line (created
            // empty; the JIT append is in flight — or its SSE push was
            // lost). Holding the CURRENT phase here pinned the screen
            // on SCAN LOCATION after the anchor scan was accepted
            // (2026-08-19 storm: pickers abandoned 20+ Large waves).
            // The picker's next real step is grabbing a tote, so show
            // that; the tote scan's sync accept reconciles the lines
            // in even without SSE.
            if (sessionLinesCount === 0) {
                setPhase("awaiting-first-tote");
            }
            return;
        }
        // The wave-complete sequence is already running (amber tick, then the
        // message, then the navigate). It owns the screen until it bounces the
        // picker out, so nothing derived from a still-settling snapshot may
        // put a picking phase back underneath it.
        if (waveCompleteHandledRef.current) return;
        setPhase(nextPhase);
        // (intentional) `session.*` reads in the body are fine — only
        // the dep array needs primitives. eslint-disable-next-line is
        // because the linter wants to add session.* objects back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localFirst, missingActive, sessionCurrentLineId, sessionCurrentQty, sessionCurrentStatus,
        sessionToteId, sessionItemsPicked, sessionTotalItems, sessionLinesCount,
        wave.pick_type, sessionStartingLocation, sessionPendingResumeTote,
        wave.require_location_scan]);

    // Lookahead queue is no longer the source of truth in local-first
    // mode — the hook derives currentLine from `lines` directly. Clear
    // it once when local-first is on so the existing line-finisher
    // optimistic-swap branch (which pops from lookaheadQueue) becomes
    // a no-op; the hook's own state update already advanced
    // currentLine. Separate effect so we don't reset the array on
    // every render.
    useEffect(() => {
        if (localFirst) setLookaheadQueue([]);
    }, [localFirst]);

    // Watch the item counter and notify usePickSpeed when it advances.
    // Centralising the trigger here means we count both local-first and
    // BE-confirmed picks, optimistic mid-line scans, and BE corrections
    // — anything that ultimately increments items_picked counts as one
    // pick for rate-tracking. Decrements (a BE rollback after rejection)
    // never trigger a record, so a rejected scan doesn't pad the rate.
    const lastItemsPickedRef = useRef(progress.itemsPicked);
    useEffect(() => {
        const prev = lastItemsPickedRef.current;
        if (progress.itemsPicked > prev) {
            for (let i = 0; i < progress.itemsPicked - prev; i++) recordPick();
            // Once per accepted scan, NOT once per unit: a qty-3 line is one
            // motion, and awarding it three windows would inflate the score.
            cycleRef.current.complete();
        }
        lastItemsPickedRef.current = progress.itemsPicked;
    }, [progress.itemsPicked, recordPick]);

    // Reset the flash icon back to idle after a beat. A successful
    // flash also kicks the idle timer — every accepted scan is "the
    // picker is alive" signal.
    const flashTimerRef = useRef<number | null>(null);
    const idleKickRef = useRef<() => void>(() => {});
    const flash = useCallback((status: Exclude<ScanStatus, "idle">) => {
        setScanStatus(status);
        if (status === "success") idleKickRef.current();
        if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
        flashTimerRef.current = window.setTimeout(() => setScanStatus("idle"), 900) as unknown as number;
    }, []);

    useEffect(() => () => {
        if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    }, []);

    // Scan-burst queue. A fast scanner can fire the next barcode before
    // the server has answered the previous one — without a queue those
    // extras get dropped (HiddenScanInput's global keydown listener
    // short-circuits while `disabled` is true, and submitScan itself
    // would also bail). Same pattern as book-in.tsx: track the in-flight
    // call via a ref and stash overflow barcodes; the `finally` block
    // drains one per round via setTimeout(0) so React can commit state
    // updates between calls (so the dequeued scan reads fresh
    // currentLine / lookaheadQueue).
    const inFlightRef = useRef(false);
    const scanQueueRef = useRef<string[]>([]);
    // Dedup: hardware scanners commonly fire the SAME barcode twice
    // within ~30-50ms when the trigger is held or the read retries.
    // Without dedup, the duplicate lands as the next scan after the
    // first completed the line — by then the BE's "next line" has
    // advanced, and the dup arrives at a line with a different SKU,
    // producing a `wrong_item` ("Item not in this line") rejection.
    // 250ms is well under the physical pace of legitimate scanning
    // (~500ms per pick + aim + fire) so legit fast scans are not
    // dropped. Time component is `performance.now()` so it's monotonic
    // (immune to wall-clock jumps).
    const lastScanRef = useRef<{ barcode: string; at: number } | null>(null);
    const DEDUP_WINDOW_MS = 250;
    // submitScan is a useCallback that references itself from the
    // `finally` drain. We bounce through this ref so the recursion
    // always points at the latest closure, even after deps change.
    const submitScanRef = useRef<(raw?: string) => void>(() => {});

    // Map a scan-response payload to local state. Centralised so all
    // three endpoints (location/item/tote) share the same merge logic.
    // The BE response is authoritative — it overrides any optimistic
    // state set before the round-trip (line swap, qty decrement).
    //
    // `lookahead` is the canonical queue. Falls back to `nextLine` for
    // older BE responses that pre-date the array shape.
    const applyResponse = useCallback((r: ScanResponse) => {
        if (r.currentLine !== undefined) {
            const nextCurrent = r.currentLine ?? null;
            const fresh = [nextCurrent, ...(r.lookahead ?? (r.nextLine ? [r.nextLine] : []))]
                .filter((l): l is ActiveWaveLine => !!l);
            setCurrentLine(prevCurrent => {
                setLegacyLines(prev => mergeLegacyLines(prev, fresh, prevCurrent, nextCurrent));
                return nextCurrent;
            });
        }
        if (Array.isArray(r.lookahead)) {
            setLookaheadQueue(r.lookahead);
        } else if (r.nextLine !== undefined) {
            setLookaheadQueue(r.nextLine ? [r.nextLine] : []);
        }
        if (r.progress) setProgress(r.progress);
    }, []);

    // ─── Skip location ─────────────────────────────────────────────
    //
    // The picker taps the location pin on the hero → prompt to scan
    // the location they are moving to (people / machines blocking the
    // aisle). The BE re-anchors the walk from the scanned point and
    // defers every bypassed location to the END of the walk, so the
    // picker sweeps back through them last. Scanning a location
    // behind the current position is a harmless no-op (skipped: 0).
    //
    // Navigational, not a pick: ALWAYS a plain round-trip. It must
    // never enter the offline replay queue — a stale skip replayed
    // minutes later would reorder the wave behind the picker's back —
    // so the pin is disabled offline and while local-first scans are
    // still syncing (same gate as manual pick / missing).
    const [skipPromptOpen, setSkipPromptOpen] = useState(false);
    const [skipBusy, setSkipBusy] = useState(false);
    // Scan routing reads the prompt through refs — the global scan
    // listener can fire against a stale closure (same gotcha as the
    // drop missing-flow's scan interception).
    const skipPromptRef = useRef(false);
    useEffect(() => { skipPromptRef.current = skipPromptOpen; }, [skipPromptOpen]);
    const skipBusyRef = useRef(false);

    const submitSkipScan = useCallback(async (barcode: string) => {
        if (skipBusyRef.current) return;
        skipBusyRef.current = true;
        setSkipBusy(true);
        try {
            const res = await apiFetch(`/picking/wave/${wave.wave_id}/skip-to-location`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode }),
            });
            if (!res.ok) {
                playError();
                flash("error");
                showToast("error", t("server_error"), { toastId: "picking-skip-net" });
                return;
            }
            const body: ScanResponse & { skipped?: number } = await res.json();
            if (!body.ok) {
                playError();
                flash("error");
                const reasonKey =
                    body.reason === "unknown_location" ? "picking_skip_unknown_location" :
                    "picking_scan_rejected";
                showToast("error", t(reasonKey), { toastId: `picking-skip-${body.reason}` });
                return;
            }
            // BE response is the authoritative new order — replace the
            // hero + lookahead queue wholesale.
            applyResponse(body);
            setSkipPromptOpen(false);
            playSuccess();
            flash("success");
            if ((body.skipped ?? 0) > 0) {
                // Real reorder: the new current location has not been
                // location-scanned, so fall back to the same phase rule
                // a cross-location hero swap uses. The BE bumped
                // wave_version — pull the local-first snapshot back in
                // line before the next locally-validated scan.
                setPhase(wave.require_location_scan ? "awaiting-location" : "awaiting-items");
                if (localFirst) await session.reconcile();
            }
            // skipped === 0 (behind-scan / current location): nothing
            // moved — keep the phase and local state exactly as-is.
        } catch {
            playError();
            flash("error");
            showToast("error", t("server_error"), { toastId: "picking-skip-err" });
        } finally {
            skipBusyRef.current = false;
            setSkipBusy(false);
        }
    }, [wave.wave_id, wave.require_location_scan, applyResponse, localFirst, session, t, flash]);

    // ─── Pause / auto-pause ────────────────────────────────────────
    //
    // The picker can manually pause via the header button. Auto-pause
    // also fires client-side after IDLE_MS of no scan activity — the
    // server-side cron is the canonical source of truth, but the
    // client timer gives a friendly toast nudge before the cron's
    // 5-minute resolution kicks in.
    const IDLE_MS = 60 * 60 * 1000; // 60 min default — matches the
                                    // warehouse setting default. The
                                    // server cron handles longer cases
                                    // and warehouse-specific overrides.

    const pauseAndExit = useCallback(async (auto: boolean) => {
        if (pausing) return;
        setPausing(true);
        try {
            const res = await apiFetch(`/picking/wave/${wave.wave_id}/pause`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            if (!res.ok) {
                playError();
                showToast("error", t("picking_pause_failed"), { toastId: "picking-pause-net" });
                return;
            }
            if (auto) {
                showToast("info", t("picking_auto_paused_toast", { minutes: String(IDLE_MS / 60000) }),
                    { toastId: "picking-auto-paused" });
            }
            onWaveComplete();
            navigate("/picking", { replace: true });
        } catch {
            playError();
            showToast("error", t("picking_pause_failed"), { toastId: "picking-pause-err" });
        } finally {
            setPausing(false);
        }
    }, [pausing, wave.wave_id, t, navigate, onWaveComplete, IDLE_MS]);

    // ─── Close wave (Singles) ──────────────────────────────────────
    //
    // A Singles wave reserves up to 80 orders up front and Pause keeps
    // every one of them on the wave, so a picker who has to stop half-
    // way used to strand the rest. The Pause tap now opens a choice:
    // Pause (unchanged) or Close wave — finished orders go to dispatch,
    // untouched orders go back to the pick queue, the wave completes.
    // The BE refuses (409 PARTIAL_ORDERS) while any order is part-
    // picked; the modal then lists those orders to finish first.
    // Large already hands untouched orders back on Pause and P&S is one
    // consolidated route, so both keep the plain pause.
    const onPauseTap = useCallback(() => {
        if (wave.pick_type === "light") setCloseModal({ step: "choice" });
        else void pauseAndExit(false);
    }, [wave.pick_type, pauseAndExit]);

    const requestCloseWave = useCallback(async () => {
        if (closing) return;
        setClosing(true);
        setCloseModal({ step: "loading" });
        try {
            const res = await apiFetch(`/picking/wave/${wave.wave_id}/close`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ dry_run: true }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body?.message ?? "preview failed");
            if (body.blocked) setCloseModal({ step: "blocked", partial: (body.partial ?? []) as PartialOrder[] });
            else setCloseModal({ step: "confirm", done: Number(body.done) || 0, untouched: Number(body.untouched) || 0 });
        } catch {
            playError();
            setCloseModal(null);
            showToast("error", t("picking_close_failed"), { toastId: "picking-close-err" });
        } finally {
            setClosing(false);
        }
    }, [closing, wave.wave_id, t]);

    const confirmCloseWave = useCallback(async () => {
        if (closing) return;
        setClosing(true);
        try {
            const res = await apiFetch(`/picking/wave/${wave.wave_id}/close`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            const body = await res.json().catch(() => ({}));
            if (res.status === 409 && body?.code === "PARTIAL_ORDERS") {
                // A pick landed between the preview and the commit.
                playError();
                setCloseModal({ step: "blocked", partial: (body.orders ?? []) as PartialOrder[] });
                return;
            }
            if (!res.ok) throw new Error(body?.message ?? "close failed");
            const count = String(body.released ?? body.released_orders ?? 0);
            showToast("info", t(body.released === true ? "picking_close_released_toast" : "picking_close_done_toast", { count }),
                { toastId: "picking-closed" });
            setCloseModal(null);
            onWaveComplete();
            navigate("/picking", { replace: true });
        } catch {
            playError();
            showToast("error", t("picking_close_failed"), { toastId: "picking-close-err" });
        } finally {
            setClosing(false);
        }
    }, [closing, wave.wave_id, t, navigate, onWaveComplete]);

    // Idle timer — only enabled while the picker is actually scanning
    // (not on wave-complete and not during the missing-item flow,
    // which has its own implicit interactivity).
    const idleEnabled =
        phase === "awaiting-resume-tote"       ||
        phase === "awaiting-starting-location" ||
        phase === "awaiting-first-tote"        ||
        phase === "awaiting-location"          ||
        phase === "awaiting-items";
    const { kick: kickIdleTimer } = usePickingIdleTimer({
        enabled: idleEnabled,
        timeoutMs: IDLE_MS,
        onIdle: () => pauseAndExit(true),
    });
    // Plumb kick into the flash() helper without making flash depend
    // on the timer's identity (avoids a re-create cascade).
    idleKickRef.current = kickIdleTimer;

    // ─── Missing-item flow ─────────────────────────────────────────
    //
    // The picker taps the "Missing item" button in the header. We
    // POST /missing/:id/start, capture the expected-SKU list returned
    // by the server, and switch the screen into the count flow:
    //   missing-location → forced location-scan gate
    //   missing-counting → live count list + Done button
    // The same HiddenScanInput dispatches into the right endpoint based
    // on phase, so the scan input doesn't unmount/remount mid-flow.

    const startMissingFlow = useCallback(async () => {
        if (submitting || !currentLine) return;
        setSubmitting(true);
        try {
            const res = await apiFetch(`/picking/missing/${wave.wave_id}/start`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ line_id: currentLine.line_id }),
            });
            if (!res.ok) {
                playError();
                showToast("error", t("server_error"), { toastId: "picking-missing-start-net" });
                return;
            }
            const body: MissingStartResponse = await res.json();
            if (!body.ok) {
                playError();
                showToast("error", t("server_error"), { toastId: "picking-missing-start-bad" });
                return;
            }
            playBoxAdd();
            setMissing({
                line: body.line,
                expected: body.expected,
                counts: {},
                stockCheckId: body.stock_check_id ?? null,
                locationDisplayName: body.location_display_name ?? body.line.location,
                stockCheckLines: body.stock_check_lines ?? [],
                pickRelevant: [],
            });
            // The picker took the location off someone else's in-progress
            // check. Tell them whose count was overridden — they may need to
            // let that person know.
            if (body.took_over_from) {
                showToast("info", t("picking_missing_took_over", { name: body.took_over_from }),
                    { toastId: "picking-missing-tookover" });
            }
            setPhase("missing-location");
        } catch {
            playError();
            showToast("error", t("server_error"), { toastId: "picking-missing-start-err" });
        } finally {
            setSubmitting(false);
        }
    }, [submitting, currentLine, wave.wave_id, t]);

    // Book a surplus find into the location, on the picker's explicit say-so.
    //
    // Deliberately routed through the Stock Check module's own /stock-check/add
    // rather than anything picking-side: that endpoint prompts nothing on its
    // own but does cost the unit from products.cost_price and stamps the same
    // "Stock check add" provenance, so stock created here is indistinguishable
    // from stock created by an operator counting the same shelf. The picking
    // flow's old auto-INSERT did neither — it copied a sibling row's cost and
    // booked at £0.00 when no sibling existed.
    const confirmSurplusAdd = useCallback(async () => {
        if (!surplusPrompt || !missing?.stockCheckId) { setSurplusPrompt(null); return; }
        setAddingSurplus(true);
        try {
            const res = await apiFetch("/stock-check/add", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    checkId: missing.stockCheckId,
                    sku: surplusPrompt.sku,
                    qty: 1,
                }),
            });
            if (!res.ok) {
                playError();
                showToast("error", t("server_error"), { toastId: "picking-surplus-add" });
                return;
            }
            playBoxAdd();
            // /add returns only { success }. Mirror the module's own screen and
            // update the list in place: bump an existing line, or append an
            // added-only one (expected 0 / scanned 1), which renders with the
            // "added" tag exactly as it does there.
            setMissing(prev => {
                if (!prev) return prev;
                const lines = prev.stockCheckLines ?? [];
                const hit = lines.some(l => l.sku === surplusPrompt.sku);
                return {
                    ...prev,
                    stockCheckLines: hit
                        ? lines.map(l => l.sku === surplusPrompt.sku
                            ? { ...l, scanned_qty: l.scanned_qty + 1 } : l)
                        : [...lines, {
                            sku: surplusPrompt.sku,
                            title: surplusPrompt.sku,
                            barcode: surplusPrompt.barcode || null,
                            expected_qty: 0,
                            scanned_qty: 1,
                            allocated_qty: 0,
                        }],
                };
            });
            setSurplusPrompt(null);
        } catch {
            playError();
            showToast("error", t("server_error"), { toastId: "picking-surplus-add" });
        } finally {
            setAddingSurplus(false);
        }
    }, [surplusPrompt, missing, t]);

    // Resolve the count. `removeUncounted` carries the picker's answer to the
    // confirm's "these weren't found - take them off the location?" question;
    // the BE only reconciles other SKUs down when it is true (see
    // reconcileLocationInventory).
    //
    // The outcome does NOT drop the picker straight back onto the pick screen
    // any more. It lands on the resolution card (phase "missing-resolved"),
    // which is the only place the next action gets stated - go and pick it
    // here, walk to the other box, or it is flagged for dispatch. Continuing
    // from the card is what returns them to picking.
    const finishMissingFlow = useCallback(async (removeUncounted: boolean) => {
        if (submitting || !missing) return;
        setSubmitting(true);
        try {
            const res = await apiFetch(`/picking/missing/${wave.wave_id}/finish`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    line_id: missing.line.line_id,
                    remove_uncounted: removeUncounted,
                }),
            });
            if (!res.ok) {
                playError();
                showToast("error", t("server_error"), { toastId: "picking-missing-finish-net" });
                return;
            }
            const body: MissingFinishResponse = await res.json();
            if (body.currentLine !== undefined) setCurrentLine(body.currentLine);
            if (Array.isArray(body.lookahead)) {
                setLookaheadQueue(body.lookahead);
            } else if (body.nextLine !== undefined) {
                setLookaheadQueue(body.nextLine ? [body.nextLine] : []);
            }
            if (body.progress) setProgress(body.progress);

            // Local-first: the hook's snapshot is now wrong in ways it cannot
            // work out for itself - the resolved line changed status, and a
            // re-slot inserted a NEW line and renumbered sort_index behind it.
            // Without this the picker was sent straight back to the box they
            // had just proved was empty, and a re-slot stayed invisible until
            // some later reconcile happened to fire. Same reconcile the
            // manual-pick path does, for the same reason.
            if (localFirst) await session.reconcile();

            if (body.resolution === "found-here") playSuccess();
            else if (body.resolution === "found-elsewhere") playBoxAdd();
            else playError();

            const sum = body.summary;
            setMissing(null);
            setFinishConfirm(false);
            setResolved({
                kind: body.resolution,
                sku: sum?.sku ?? missing.line.sku,
                // Both summary.location and line.location are raw barcodes;
                // the count screens already showed the display name — keep it.
                location: missing.locationDisplayName
                    || (sum?.location ?? missing.line.location),
                foundQty: sum?.foundQty ?? 0,
                requiredQty: sum?.requiredQty ?? 0,
                keptHereQty: sum?.keptHereQty ?? 0,
                redirectLocation: sum?.redirectLocationDisplay
                    ?? body.redirectLocationDisplay
                    ?? sum?.redirectLocation
                    ?? body.redirectLocation
                    ?? null,
                redirectIsNextStop: sum?.redirectIsNextStop ?? false,
                flaggedOrders: sum?.flaggedOrders ?? 0,
                flaggedQty: sum?.flaggedQty ?? 0,
                ordersFlaggedForReview: sum?.ordersFlaggedForReview ?? 0,
                waveComplete: !!body.waveComplete,
            });
            resolutionOpenRef.current = true;
            setPhase("missing-resolved");
        } catch {
            playError();
            showToast("error", t("server_error"), { toastId: "picking-missing-finish-err" });
        } finally {
            setSubmitting(false);
        }
    }, [submitting, missing, wave.wave_id, t, localFirst, session]);

    // Picker acknowledged the outcome. In local-first the reconcile above has
    // already landed, so clearing `resolved` re-enables the mirror effect and
    // it derives phase from the authoritative session state; on the server
    // path we derive it from the response's currentLine as before.
    const continueFromResolution = useCallback(() => {
        const wasComplete = resolved?.waveComplete || waveCompleteDeferredRef.current;
        // Clear the ref synchronously — scheduleWaveCompleteNavigate reads it
        // and would otherwise re-defer the completion it was just handed.
        resolutionOpenRef.current = false;
        waveCompleteDeferredRef.current = false;
        setResolved(null);
        if (wasComplete) {
            scheduleWaveCompleteNavigate();
            return;
        }
        if (!localFirst) {
            setPhase(initialPhaseFor(currentLine, !!currentToteId, false, wave.require_location_scan));
        }
    }, [resolved, localFirst, currentLine, currentToteId, wave.require_location_scan,
        scheduleWaveCompleteNavigate]);

    // Abandon the count. Nothing was resolved server-side (the line kept its
    // pending/picking status throughout), so the wave is exactly as it was.
    const cancelMissingFlow = useCallback(() => {
        setMissing(null);
        setFinishConfirm(false);
        if (!localFirst) {
            setPhase(initialPhaseFor(currentLine, !!currentToteId, false, wave.require_location_scan));
        }
    }, [localFirst, currentLine, currentToteId, wave.require_location_scan]);

    // Units the flagged line still owes — what "found it" has to beat, and
    // what the gate tells the picker to look for. Across the whole group of
    // lines for this SKU at this location when the BE says so: the hero
    // showed the picker that number, and /finish resolves the whole group.
    const missingQtyOutstanding = missing
        ? (missing.line.group_qty_outstanding
            ?? Math.max(0, Number(missing.line.qty_required) - Number(missing.line.qty_picked)))
        : 0;

    // The count has found everything the flagged line still owes.
    const missingTargetFound = !!missing
        && missingQtyOutstanding > 0
        && (missing.counts[missing.line.sku] ?? 0) >= missingQtyOutstanding;

    // Other SKUs at this location the count did not account for. Reserved
    // units are excluded - unscanned does not mean lost for those (they may be
    // in a tote), the same split the Stock Check module's write-off uses. The
    // target SKU is excluded too: the flow always reconciles it, so it is
    // never part of the optional "remove these as well?" decision.
    const uncountedLines: UncountedLine[] = (missing?.stockCheckLines ?? [])
        .filter(l => l.sku !== missing?.line.sku)
        .map(l => ({
            sku: l.sku,
            title: l.title,
            qty: Math.max(0, (l.expected_qty - l.scanned_qty) - Number(l.allocated_qty ?? 0)),
        }))
        .filter(l => l.qty > 0);

    // Manual pick — the "barcode not scanning" fallback. The hand
    // button on PickingScreen's dark action bar opens ManualPickModal
    // for the HERO line only (anything else at the location waits its
    // turn); confirming posts the whole quantity in ONE call to
    // /pick-item-manual. The BE validates that the line is still the
    // wave's current line and that qty ≤ remaining, then runs the same
    // bookkeeping as that many accepted item scans (audited as
    // pick.manual).
    const [manualPickOpen, setManualPickOpen] = useState(false);
    const [manualBusy, setManualBusy] = useState(false);
    const confirmManualPick = useCallback(async (qty: number) => {
        if (manualBusy || !currentLine) return;
        // Local-first: a scan from before the modal opened may still be
        // syncing. The manual POST takes the classic path and the
        // reconcile after it snapshots BE state WITHOUT that queued
        // scan — local state would under-count until the sync's own
        // reconcile heals it. Hold the confirm until the backlog
        // drains (the button is also disabled on backlog; this guard
        // covers a sync that started right before the modal opened).
        if (localFirst && session.syncBacklog > 0) {
            playError();
            showToast("error", t("picking_sync_wait"), { toastId: "picking-manual-sync" });
            return;
        }
        setManualBusy(true);
        try {
            const res = await apiFetch(`/picking/wave/${wave.wave_id}/pick-item-manual`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    line_id: currentLine.line_id,
                    qty,
                    // Same idempotency contract as scans — a network
                    // retry of a confirm that actually landed can never
                    // double-pick.
                    client_scan_id: typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
                        ? crypto.randomUUID()
                        : `${Date.now()}-manual-${Math.random().toString(36).slice(2)}`,
                }),
            });
            if (!res.ok) {
                playError(); flash("error");
                showToast("error", t("server_error"), { toastId: "picking-manual-net" });
                return;
            }
            const body: ScanResponse = await res.json();
            if (!body.ok) {
                playError(); flash("error");
                // Resume gate (wave paused elsewhere while the modal was
                // open) — name WHICH tote unlocks the wave, like the
                // scan path does.
                if (body.reason === "scan_resume_tote_first") {
                    showToast("error",
                        t("picking_scan_resume_tote_first", { tote: body.expected ?? "" }),
                        { toastId: "picking-resume-tote" });
                    return;
                }
                const reasonKey =
                    body.reason === "not_current_line"      ? "picking_manual_stale"        :
                    body.reason === "invalid_qty"           ? "picking_manual_qty_invalid"  :
                    body.reason === "line_already_complete" ? "picking_manual_stale"        :
                    body.reason === "scan_location_first"   ? "picking_scan_location_first" :
                    body.reason === "scan_tote_first"       ? scanToteFirstKey     :
                    "picking_scan_rejected";
                showToast("error", t(reasonKey), { toastId: `picking-manual-${body.reason}` });
                // "Stale" means the hero is showing a line the BE has
                // already moved past — the toast tells the picker to
                // check the screen, so the screen must actually catch
                // up. Without this refetch they were stuck re-trying the
                // same line until some later scan was rejected and its
                // reconcile healed it (2026-10-06, wave 10105 / N27L3:
                // two manual attempts and a rescan, ~90 s lost).
                if (localFirst && (body.reason === "not_current_line" || body.reason === "line_already_complete")) {
                    await session.reconcile();
                }
                return;
            }
            // Green banner: a manual pick is always the hero line (the
            // BE's pickedLine echo is authoritative but identical).
            setLastPicked(toLastPicked(body.pickedLine ?? currentLine));
            // The manual endpoint always takes the classic full-response
            // path, even on local-first warehouses — so in local-first
            // mode the session hook's snapshot is now stale. Reconcile
            // it from /picking/active; the mirror effect then advances
            // currentLine / phase / progress. In server mode the
            // response itself is authoritative — merge it like any scan.
            if (localFirst) {
                await session.reconcile();
            } else {
                applyResponse(body);
            }
            if (body.waveComplete) {
                scheduleWaveCompleteNavigate();
                return;
            }
            if (body.lineItemsComplete && body.awaitingTote) {
                // Large: this manual pick finished the order — the BE
                // closed the tote; prompt for a fresh one.
                beginOrderCompleteTote();
                return;
            }
            if (body.lineItemsComplete) {
                playSuccess(); flash("success");
                // Same location-scan gate as a line-finishing scan:
                // skip the re-scan when the next line is at the same
                // physical location. (Local-first derives phase in the
                // session mirror effect instead.)
                if (!localFirst && body.advancedLineId !== undefined && body.currentLine) {
                    const sameLoc = body.currentLine.location === currentLine.location;
                    setPhase(sameLoc || !wave.require_location_scan ? "awaiting-items" : "awaiting-location");
                }
            } else {
                playBoxAdd(); flash("success");
            }
        } catch {
            playError(); flash("error");
            showToast("error", t("server_error"), { toastId: "picking-manual-err" });
        } finally {
            setManualBusy(false);
            setManualPickOpen(false);
        }
    }, [manualBusy, currentLine, wave.wave_id, wave.require_location_scan, localFirst, session,
        applyResponse, flash, t, scheduleWaveCompleteNavigate, beginOrderCompleteTote]);

    // Submit a scan to the chosen endpoint. Only one in flight at a
    // time so a fast-typing scanner can't race itself.
    //
    // For most phases the tick/X icon and the success/error sound stay
    // authoritative — they only fire after the BE confirms or rejects
    // the scan. An earlier iteration played the success cue immediately
    // for every phase and corrected on failure, which felt worse than
    // the round-trip wait (a "yes, no" sequence on bad scans was
    // actively confusing).
    //
    // Mid-line item scans (qtyRemaining > 1) DO fire the success cue
    // optimistically: the picker has already scanned this exact SKU at
    // this exact location at least once and the BE accepted it, so the
    // next repeat is overwhelmingly going to be accepted too. On the
    // rare rejection (partial bad scan), the existing rollback path
    // restores qty and plays the X — same correction we use everywhere
    // else, just on top of a brief green tick. Line-finishers, location
    // scans, tote scans, and starting-location scans all keep the
    // authoritative wait-for-BE behaviour because their downstream
    // state (line complete, JIT order append, wave complete) can't be
    // pre-celebrated without confusing the picker.
    //
    // The hero is also updated optimistically on a line-finishing item
    // scan: that one's almost always going to be accepted, and
    // pre-rendering the next location/sku eliminates the perceived
    // wait between picks. If the BE rejects, the rollback path
    // restores currentLine + the lookahead queue from the snapshot.
    // "Finish this order first?" card (Singles tote swap mid-order).
    // Keep picking = drop the swap; the open tote is unchanged on the BE
    // (it rolled the swap back), so nothing to undo here.
    const dismissSwapGuard = useCallback(() => {
        swapGuardRef.current = null;
        setSwapGuard(null);
    }, []);

    // Swap anyway: the remaining units don't fit, so the order WILL be
    // split. Re-post the tote scan with force straight to /scan-tote
    // (the only endpoint that honours it), then apply the BE's answer
    // exactly as a normal swap would. Under local-first the hook never
    // saw this command, so reconcile afterwards to pull the new open
    // tote into its state.
    const confirmSwapAnyway = useCallback(async () => {
        const guard = swapGuardRef.current;
        if (!guard || swapForcing) return;
        setSwapForcing(true);
        try {
            const res = await apiFetch(`/picking/wave/${wave.wave_id}/scan-tote`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: guard.barcode, force: true }),
            });
            if (!res.ok) {
                playError();
                showToast("error", t("server_error"), { toastId: "picking-swap-force-err" });
                return;
            }
            const body: ScanResponse = await res.json();
            if (!body.ok) {
                playError(); flash("error");
                const reasonKey =
                    body.reason === "tote_unavailable"     ? "picking_tote_unavailable"     :
                    body.reason === "tote_in_use_elsewhere" ? "picking_tote_in_use_elsewhere" :
                    "picking_scan_rejected";
                showToast("error", t(reasonKey), { toastId: `picking-${body.reason}` });
                dismissSwapGuard();
                return;
            }
            if (body.currentToteId) setCurrentToteId(body.currentToteId);
            playToteFinalised();
            flash("success");
            showToast("info",
                t("picking_tote_swapped", { tote: body.currentToteId ?? guard.barcode }),
                { toastId: "picking-tote-swapped" },
            );
            dismissSwapGuard();
            if (localFirst) await session.reconcile();
        } catch {
            playError();
            showToast("error", t("server_error"), { toastId: "picking-swap-force-err" });
        } finally {
            setSwapForcing(false);
        }
    // flash is stable after first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [swapForcing, wave.wave_id, t, localFirst, session, dismissSwapGuard]);

    const submitScan = useCallback(async (raw?: string) => {
        const candidate = raw ?? scanValue;
        const barcode = candidate.trim();
        if (!barcode) return;

        // "Finish this order first?" card is up. Scanning the same york
        // again is the scanner-first "swap anyway"; any other scan means
        // the picker carried on (keep picking) — drop the card and let
        // the scan through as normal.
        if (swapGuardRef.current) {
            if (barcode === swapGuardRef.current.barcode) {
                setScanValue("");
                void confirmSwapAnyway();
                return;
            }
            dismissSwapGuard();
        }

        // Drop scanner-double-fires: same barcode within DEDUP_WINDOW_MS
        // of the previous one. Drains the second copy silently — the
        // first is already in flight (or about to be), and processing
        // the duplicate would land on whatever line came AFTER the
        // line the first completes, almost always failing with
        // `wrong_item`. Legit fast picking (one physical pick per
        // scan) runs at >250ms per scan even at speed, so this only
        // affects double-fires.
        const now = performance.now();
        if (lastScanRef.current
            && lastScanRef.current.barcode === barcode
            && (now - lastScanRef.current.at) < DEDUP_WINDOW_MS) {
            return;
        }
        lastScanRef.current = { barcode, at: now };

        // Skip-location prompt is open — this scan answers "scan the
        // location you're moving to", not a pick. Route it to the
        // skip endpoint and swallow it from every other flow
        // (including local-first, which would locally reject a
        // different location as wrong_location).
        if (skipPromptRef.current) {
            setScanValue("");
            void submitSkipScan(barcode);
            return;
        }

        // Local-first dispatch — the hook owns scan state. processScan
        // returns synchronously with the local verdict; the BE sync
        // happens fire-and-forget inside the hook. UI feedback (flash
        // + audio) fires immediately off the local verdict.
        if (localFirst && !missing) {
            const r = session.processScan(barcode);
            setScanValue("");
            if (r.kind === "rejected") {
                playError();
                flash("error");
                // Resume-tote gate — show WHICH tote unlocks the wave.
                if (r.reason === "scan_resume_tote_first") {
                    showToast("error",
                        t("picking_scan_resume_tote_first", { tote: r.expected ?? pendingResumeTote ?? "" }),
                        { toastId: "picking-resume-tote" });
                    return;
                }
                // Every rejection gets a toast — an X with no explanation
                // leaves the picker guessing what went wrong. Unmapped /
                // novel reasons fall back to the generic "Scan rejected".
                const reasonKey =
                    r.reason === "wrong_location"          ? "picking_wrong_location"             :
                    r.reason === "wrong_item"              ? "picking_wrong_item"                 :
                    r.reason === "scan_location_first"     ? "picking_scan_location_first"        :
                    r.reason === "scan_tote_first"         ? scanToteFirstKey            :
                    r.reason === "scan_starting_location_first" ? "picking_scan_starting_location_first" :
                    r.reason === "line_already_complete"   ? "picking_wrong_item"                 :
                    r.reason === "unknown_barcode"         ? "picking_unknown_barcode"            :
                    r.reason === "needs_reconciliation"    ? "picking_sync_wait"                  :
                    // Local snapshot has no open line — mid-wave (Large
                    // between orders) that's a stale snapshot, and the
                    // hook has already kicked a reconcile. Tell the
                    // picker to retry in a beat, not "rejected".
                    r.reason === "wave_complete"           ? "picking_sync_wait"                  :
                    "picking_scan_rejected";
                showToast("error", t(reasonKey), { toastId: `picking-${r.reason}` });
                return;
            }
            // Record the picked unit for the green banner BEFORE the
            // waveComplete / orderComplete early-returns so an order-
            // or wave-finishing pick still lands on it. r.lineId points
            // at whichever line the scan actually resolved to — the
            // hero line or a same-location sibling
            // (scan-any-item-at-location).
            if (r.type === "item" && r.lineId != null) {
                const picked = session.lines.find(l => l.line_id === r.lineId);
                if (picked) setLastPicked(toLastPicked(picked));
            }
            // Accepted — play the right cue + flash. waveComplete
            // funnels through the centralized scheduler so the
            // local-first SSE-driven path (Single/Large) can't
            // double-fire.
            if (r.waveComplete) {
                scheduleWaveCompleteNavigate();
                return;
            }
            // Large: order finished (wave continues). Show the tick, then
            // the Scan Tote prompt for the next order. The BE closes the
            // filled tote; the background refetch lands the next order so
            // it's ready once the picker scans the new tote.
            if (r.orderComplete) {
                beginOrderCompleteTote();
                return;
            }
            // Tote + starting-location scans are accepted optimistically
            // for state, but the FE can't validate them (tote
            // availability / walk-order) — so DON'T play a cue here. The
            // good beep fires from onSyncAccept once the BE confirms, and
            // the X + bad beep from onSyncReject if it rejects. This kills
            // the old "positive beep, then negative beep" on a bad tote
            // or starting-location scan.
            if (r.type === "tote" || r.type === "starting_location") {
                return;
            }
            // Celebratory "line done" only when the whole SKU-at-location
            // group is finished. A split-batch SKU (one wave line per
            // inventory batch) completes its first line while the hero's
            // combined counter still wants more from the same shelf —
            // playing the done cue there told pickers to move on and
            // then "sent them back" (2026-10-06, wave 10112). Older hook
            // results without the flag keep the old behaviour.
            if (r.lineComplete && r.groupComplete !== false) {
                playSuccess();
                flash("success");
            } else {
                // accepted item/location scan (locally validated), a
                // mid-group batch-line finish, or an idempotent rescan of
                // an already-picking location
                playBoxAdd();
                flash("success");
            }
            return;
        }

        // Already processing the previous scan — stash this barcode and
        // let the in-flight call's `finally` drain it. Without this,
        // fast bursts get dropped while the fetch is in flight (the
        // global keydown listener short-circuits on `disabled`, and
        // even if it didn't, the previous version bailed on
        // `submitting`).
        if (inFlightRef.current) {
            scanQueueRef.current.push(barcode);
            return;
        }
        inFlightRef.current = true;
        setSubmitting(true);

        // Snapshot the pre-scan state for rollback on BE rejection.
        let didOptimisticSwap = false;
        const swapSnapshot = { currentLine, lookaheadQueue, phase, lastPicked };
        const isAwaitingItems = phase === "awaiting-items" && currentLine !== null;
        const qtyRemaining = currentLine ? currentLine.qty_required - currentLine.qty_picked : 0;
        const queueHead = lookaheadQueue[0] ?? null;
        // Optimistic hero-swap / qty++ only when the barcode is one of
        // the CURRENT line's known identifiers. Other scans can still
        // be accepted by the BE — an item belonging to a sibling line
        // at the same location (scan-any-item-at-location), or a tote
        // swap — but they don't advance the current line, so treating
        // them optimistically as a current-line pick would flash the
        // wrong hero/qty and snap back when the response lands. Those
        // wait for the BE verdict (cue fires on confirmation below).
        // Master-SKU scans (not carried on the line) also take the
        // wait-for-BE path — a ~200ms cue delay, still correct.
        const matchesCurrentLine = !!currentLine && (
            barcode === currentLine.sku
            || barcode === currentLine.product_barcode
            || barcode === currentLine.product_barcode_secondary);
        const isLineFinisher = isAwaitingItems && matchesCurrentLine && qtyRemaining === 1;
        if (isLineFinisher) {
            if (queueHead) {
                // Pop the head off the lookahead queue and into
                // currentLine instantly so the hero shows the next
                // location/SKU even before the BE round-trip completes.
                //
                // If the next line is a batch-sibling (same SKU + location
                // — the multi-batch split that the combined hero counter
                // exists for), the queued line's group_qty_prior_picked is
                // a stale snapshot from before this line finished. By
                // definition every earlier member of the group is now
                // fully picked, so prior_picked == group_qty_required −
                // qty_required exactly. Set it so the combined counter
                // advances smoothly (…/10) instead of flickering down a
                // unit until the BE response lands.
                const advancingWithinGroup =
                    queueHead.sku === currentLine?.sku
                    && queueHead.location === currentLine?.location;
                const head = advancingWithinGroup && queueHead.group_qty_required != null
                    ? { ...queueHead, group_qty_prior_picked: queueHead.group_qty_required - queueHead.qty_required }
                    : queueHead;
                // Same staleness for the location-level counter: a
                // same-location queue head was snapshotted before this
                // finishing unit landed, so "Units at this location"
                // would dip back a unit until the BE response arrives.
                // Bump it by the one unit this scan just picked.
                const headWithLocation =
                    queueHead.location === currentLine?.location && head.location_qty_picked != null
                        ? { ...head, location_qty_picked: head.location_qty_picked + 1 }
                        : head;
                setCurrentLine(headWithLocation);
                setLookaheadQueue(lookaheadQueue.slice(1));
                // Move phase optimistically in lockstep with the hero
                // swap so the sub-prompt updates together — without
                // this, the picker briefly sees the next location/SKU
                // with the old "SCAN ITEMS" prompt until the BE
                // response arrives. If the next line is at the SAME
                // physical location as the line we just completed
                // (multi-SKU at one bay, Single waves, etc.) skip the
                // redundant location re-scan and stay in awaiting-items.
                // The BE has matching same-location auto-transition
                // logic in scanItem so queued item scans land
                // correctly. Cross-location transitions still require
                // an explicit location scan.
                const sameLoc = queueHead.location === currentLine?.location;
                if (sameLoc || !wave.require_location_scan) {
                    setPhase("awaiting-items");
                } else {
                    setPhase("awaiting-location");
                }
            } else {
                // Last queued line — wave is ABOUT to finish for
                // Pick & Sort and Single (both built upfront, no more
                // lines coming), but Large can still JIT-append the
                // next order on the BE's completion path. Pin the hero
                // to the just-picked line with qty satisfied so the
                // picker doesn't see a blank hero during the round-trip.
                setCurrentLine({ ...currentLine!, qty_picked: currentLine!.qty_required });
                // Only flip to "wave-complete" optimistically for the
                // upfront-built types. For Large, flipping here would
                // show an amber wave-complete tick that the BE
                // response then has to undo when it JIT-appends the
                // next Large order. Leave phase as "awaiting-items";
                // the BE response decides:
                //   • waveComplete=true → flip via the branch below
                //   • advancedLineId + new currentLine → swap line,
                //     keep awaiting-items / set awaiting-location
                if (wave.pick_type === "pick_and_sort" || wave.pick_type === "light") {
                    setPhase("wave-complete");
                }
            }
            // Fire the green tick immediately so the picker sees the
            // confirm cue without waiting ~200ms for the BE response.
            // The celebratory `playSuccess()` still waits for BE
            // confirmation (line 732) — line-completion sound shouldn't
            // play if the BE rejects, but the visual tick is cheap to
            // roll back to an X on rejection (see the !res.ok and catch
            // branches below).
            // Pre-swap currentLine is the line this scan just finished —
            // record it on the green banner optimistically (rolled back
            // with the rest of the snapshot on rejection).
            setLastPicked(toLastPicked(currentLine!));
            playBoxAdd();
            flash("success");
            didOptimisticSwap = true;
        } else if (isAwaitingItems && matchesCurrentLine && qtyRemaining > 1) {
            // Mid-line repeat. Optimistically increment qty AND fire
            // the success cue immediately — see the long comment above
            // for why this is safe here but not on line-finishers. The
            // location-level counter moves in lockstep (same one unit).
            setCurrentLine({
                ...currentLine!,
                qty_picked: currentLine!.qty_picked + 1,
                ...(currentLine!.location_qty_picked != null
                    ? { location_qty_picked: currentLine!.location_qty_picked + 1 }
                    : {}),
            });
            setLastPicked(toLastPicked(currentLine!));
            didOptimisticSwap = true;
            playBoxAdd();
            flash("success");
        }

        try {
            // Pick endpoint by phase. scan-location and scan-item both
            // accept any-time tote scans (the BE detects a known tote
            // and either swaps the wave's open tote or rejects with
            // tote_unavailable). awaiting-first-tote uses scan-tote
            // directly since only a tote scan moves the wave forward
            // from that state.
            let endpoint: string | null = null;
            let payload: Record<string, unknown> = { barcode };
            // awaiting-starting-location uses the same /scan-location
            // endpoint — the BE detects "wave has no starting_location
            // yet" and treats the scan as the starting anchor.
            if (phase === "awaiting-starting-location") endpoint = `/picking/wave/${wave.wave_id}/scan-location`;
            else if (phase === "awaiting-resume-tote") endpoint = `/picking/wave/${wave.wave_id}/scan-tote`;
            else if (phase === "awaiting-first-tote") endpoint = `/picking/wave/${wave.wave_id}/scan-tote`;
            else if (phase === "awaiting-location")   endpoint = `/picking/wave/${wave.wave_id}/scan-location`;
            else if (phase === "awaiting-items")      endpoint = `/picking/wave/${wave.wave_id}/scan-item`;
            else if (phase === "missing-location" && missing) {
                endpoint = `/picking/missing/${wave.wave_id}/scan-location`;
                payload = { barcode, line_id: missing.line.line_id };
            }
            else if (phase === "missing-counting" && missing) {
                endpoint = `/picking/missing/${wave.wave_id}/scan-item`;
                // stock_check_id mirrors the scan onto the real stock check.
                payload = {
                    barcode, line_id: missing.line.line_id,
                    stock_check_id: missing.stockCheckId,
                };
            }
            if (!endpoint) return;

            const res = await apiFetch(endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                if (didOptimisticSwap) {
                    setCurrentLine(swapSnapshot.currentLine);
                    setLookaheadQueue(swapSnapshot.lookaheadQueue);
                    setPhase(swapSnapshot.phase);
                    setLastPicked(swapSnapshot.lastPicked);
                }
                playError();
                flash("error");
                showToast("error", t("server_error"), { toastId: "picking-scan-net" });
                return;
            }
            // Missing-flow scans use a slightly different response shape
            // (sku/counts vs currentLine/progress). Branch the body type
            // based on phase.
            if (phase === "missing-location" || phase === "missing-counting") {
                const mb: MissingScanResponse = await res.json();
                if (!mb.ok) {
                    playError();
                    flash("error");
                    const reasonKey =
                        mb.reason === "wrong_location"   ? "picking_missing_wrong_location"  :
                        mb.reason === "unknown_barcode"  ? "picking_missing_unknown_barcode" :
                        "picking_scan_rejected";
                    showToast("error", t(reasonKey), { toastId: `picking-missing-${mb.reason}` });
                    return;
                }
                // Surplus: a unit the count can't book on its own — a SKU
                // this location doesn't stock (known_not_here), an expected
                // SKU past its expected count (surplus), or another unit of
                // a SKU added earlier this session (already_added). The
                // classifier records none of these, so without the prompt the
                // scan chimed "success" and disappeared: no row on the list,
                // no change to the running total, nothing the picker could
                // act on — multi-unit finds were silently booked as 1
                // (W25L7, 2026-08-21). Ask first.
                if (phase === "missing-counting" && mb.sku
                    && (mb.stock_check_outcome === "known_not_here"
                        || mb.stock_check_outcome === "surplus"
                        || mb.stock_check_outcome === "already_added")) {
                    playError();
                    flash("error");
                    setSurplusPrompt({
                        sku: mb.sku,
                        barcode: String(payload.barcode ?? ""),
                        extraUnit: mb.stock_check_outcome !== "known_not_here",
                    });
                    return;
                }
                playBoxAdd();
                flash("success");
                if (phase === "missing-location") {
                    setPhase("missing-counting");
                } else {
                    setMissing(prev => prev ? {
                        ...prev,
                        counts: mb.counts ?? prev.counts,
                        stockCheckLines: mb.stock_check_lines ?? prev.stockCheckLines,
                        // Accumulate, de-duped by SKU: the picker may scan the
                        // same pick-relevant item several times during a count
                        // and should see it listed once.
                        pickRelevant: mb.pick_relevant
                            && !prev.pickRelevant.some(p => p.sku === mb.pick_relevant!.sku)
                            ? [...prev.pickRelevant, mb.pick_relevant]
                            : prev.pickRelevant,
                    } : prev);
                    // Flag it at scan time too — this item is on their own pick
                    // list, so they need to know before they walk away.
                    if (mb.pick_relevant) {
                        showToast("info", t("picking_missing_pick_relevant", {
                            sku: mb.pick_relevant.sku,
                            location: mb.pick_relevant.location,
                        }), { toastId: `picking-missing-relevant-${mb.pick_relevant.sku}` });
                    }
                }
                return;
            }

            const body: ScanResponse = await res.json();
            applyResponse(body);

            // JIT Single/Large pull in the next order as the picker
            // completes each one. The hero advances seamlessly to the
            // new line, which is feedback enough — no toast needed
            // (it fired on every order completion and quickly became
            // noise rather than signal).

            if (!body.ok) {
                // BE-level rejection (wrong barcode, wrong location, etc).
                // Error responses don't carry currentLine/lookahead, so
                // applyResponse() above was a no-op — but our optimistic
                // swap is still showing the next line. Roll it back so
                // the picker sees the line they actually need to finish.
                if (didOptimisticSwap) {
                    setCurrentLine(swapSnapshot.currentLine);
                    setLookaheadQueue(swapSnapshot.lookaheadQueue);
                    setPhase(swapSnapshot.phase);
                    setLastPicked(swapSnapshot.lastPicked);
                }
                // Singles tote swap mid-order — a question, not a bad
                // scan: no error cue, the card is the feedback.
                if (body.reason === "order_part_picked" && typeof body.orderId === "string") {
                    setSwapGuard({
                        barcode,
                        order: {
                            orderId: body.orderId,
                            toteId: body.toteId ?? "",
                            qtyPicked: Number(body.qtyPicked ?? 0),
                            qtyRequired: Number(body.qtyRequired ?? 0),
                            remaining: Array.isArray(body.remaining) ? body.remaining : [],
                        },
                    });
                    return;
                }
                playError();
                flash("error");
                // tote_in_use_elsewhere has identified variants when
                // the BE returns who's holding the tote — show the
                // picker name + wave so the team knows who to chase.
                // Resume-tote gate — show WHICH tote unlocks the wave.
                if (body.reason === "scan_resume_tote_first") {
                    showToast("error",
                        t("picking_scan_resume_tote_first", { tote: body.expected ?? pendingResumeTote ?? "" }),
                        { toastId: "picking-resume-tote" });
                    return;
                }
                if (body.reason === "tote_in_use_elsewhere") {
                    let msg: string;
                    if (body.heldByModule === "drop" && body.heldByPickerName) {
                        msg = t("picking_tote_held_by_drop_picker", { picker: body.heldByPickerName });
                    } else if (body.heldByModule === "drop") {
                        msg = t("picking_tote_held_by_drop");
                    } else if (body.heldByPickerName && body.heldByWaveId != null) {
                        msg = t("picking_tote_held_by_picker", {
                            picker: body.heldByPickerName,
                            wave:   String(body.heldByWaveId),
                        });
                    } else {
                        msg = t("picking_tote_in_use_elsewhere");
                    }
                    showToast("error", msg, { toastId: `picking-${body.reason}` });
                    return;
                }
                // Starting-location barcode that lives in ANOTHER
                // warehouse — name it (interpolated, so handled before
                // the plain key map).
                if (body.reason === "wrong_warehouse") {
                    showToast("error",
                        t("scan_wrong_warehouse", { warehouse: body.locationWarehouse ?? "?" }),
                        { toastId: "picking-wrong-warehouse" });
                    return;
                }
                // Unmapped reasons fall back to the generic "Scan
                // rejected" — a red X with no message leaves the picker
                // guessing.
                const reasonKey =
                    body.reason === "wrong_location"             ? "picking_wrong_location"             :
                    body.reason === "wrong_item"                 ? "picking_wrong_item"                 :
                    body.reason === "unknown_tote"               ? "picking_unknown_tote"               :
                    body.reason === "scan_location_first"        ? "picking_scan_location_first"        :
                    body.reason === "scan_tote_first"            ? scanToteFirstKey            :
                    body.reason === "tote_unavailable"           ? "picking_tote_unavailable"           :
                    body.reason === "single_tote_order"          ? "picking_single_tote_order"          :
                    body.reason === "tote_not_york"              ? "picking_tote_not_york"              :
                    body.reason === "invalid_starting_location"  ? "picking_invalid_starting_location"  :
                    body.reason === "scan_starting_location_first" ? "picking_scan_starting_location_first" :
                    body.reason === "line_already_complete"      ? "picking_wrong_item"                 :
                    "picking_scan_rejected";
                showToast("error", t(reasonKey), { toastId: `picking-${body.reason}` });
                return;
            }

            // Wave finished — handle BEFORE the phase-specific branches
            // below. For Pick & Sort / Single (upfront-allocated) the
            // optimistic line-finisher already flipped `phase` to
            // "wave-complete" the instant the last line was scanned, so
            // the `phase === "awaiting-items"` branch further down is
            // skipped and never schedules the navigate — leaving the
            // picker stuck on the yellow tick. Checking the BE's
            // authoritative `waveComplete` here, independent of phase,
            // fixes that. scheduleWaveCompleteNavigate is idempotent
            // (waveCompleteHandledRef), so Large's SSE-driven path and
            // the tote-registration branch below are unaffected.
            if (body.waveComplete) {
                scheduleWaveCompleteNavigate();
                return;
            }

            // Large: an order finished and the BE closed its tote. Show
            // the order-complete tick for 500ms, then prompt for a fresh
            // tote to start the next order — also handled before the
            // phase branches so we don't advance straight to the next
            // line.
            if (body.lineItemsComplete && body.awaitingTote) {
                beginOrderCompleteTote();
                return;
            }

            // Tote registration — the BE recognised the barcode as a
            // known tote and either opened it or swapped from the
            // previous tote. Tick/sound fire here, after BE confirms.
            if (body.toteRegistered !== undefined || body.currentToteId !== undefined) {
                if (body.currentToteId) setCurrentToteId(body.currentToteId);
                if (body.waveComplete) {
                    scheduleWaveCompleteNavigate();
                    return;
                }
                // Resume-tote verification cleared — the picker scanned
                // the pause-time tote. Drop straight into the normal
                // flow (skip the "already open" info toast below: this
                // is the expected confirmation, not a redundant rescan).
                if (phase === "awaiting-resume-tote") {
                    setPendingResumeTote(null);
                    playBoxAdd();
                    flash("success");
                    setPhase(initialPhaseFor(body.currentLine ?? currentLine, true, false, wave.require_location_scan));
                    return;
                }
                if (body.toteSwapped) {
                    playToteFinalised();
                    flash("success");
                    showToast("info",
                        t("picking_tote_swapped", { tote: body.currentToteId ?? "" }),
                        { toastId: "picking-tote-swapped" },
                    );
                } else if (!body.toteRegistered && body.currentToteId) {
                    // Rescan of the currently-open tote — no state change.
                    playBoxAdd();
                    flash("success");
                    showToast("info",
                        t("picking_tote_already_open", { tote: body.currentToteId }),
                        { toastId: "picking-tote-already-open", className: "toast-neutral" },
                    );
                } else {
                    // Fresh tote opened.
                    playBoxAdd();
                    flash("success");
                }
                if (phase === "awaiting-first-tote") {
                    // First tote of the wave opened — drop into the
                    // normal pick flow at the current line.
                    setPhase(initialPhaseFor(body.currentLine ?? currentLine, true, false, wave.require_location_scan));
                }
                return;
            }

            // Starting-location response — picker just anchored
            // themselves. The BE now defers the first-order
            // computation to a background task (so this response can
            // come back in <100ms instead of 300-700ms), which means
            // `currentLine` may be NULL on the response. That's not
            // wave-complete — async is computing it; the tote-scan
            // response will surface it. Pin the phase to
            // awaiting-first-tote in that case so the FE shows the
            // tote prompt and the picker moves on.
            //
            // When `currentLine` IS populated (the BE ran ensureLookahead
            // synchronously because a tote was already open, or this is
            // the rare same-response case), `initialPhaseFor` decides
            // the next phase normally.
            if (phase === "awaiting-starting-location") {
                playBoxAdd();
                flash("success");
                const toteOpen = body.currentToteId ?? currentToteId;
                const line = body.currentLine ?? currentLine;
                if (!line) {
                    setPhase("awaiting-first-tote");
                } else {
                    setPhase(initialPhaseFor(
                        line,
                        !!toteOpen,
                        false,
                        wave.require_location_scan,
                    ));
                }
                return;
            }

            // Item / location scan transitions. Fire the confirm cue
            // now that the BE has accepted the scan.
            if (phase === "awaiting-location") {
                playBoxAdd();
                flash("success");
                setPhase("awaiting-items");
            } else if (phase === "awaiting-items") {
                // Authoritative picked-line identity from the BE —
                // replaces the optimistic set above and, crucially,
                // covers the paths that never set it optimistically:
                // sibling-line picks (scan-any-item-at-location) and
                // barcodes not carried on the line (master SKU).
                if (body.pickedLine) {
                    setLastPicked(toLastPicked(body.pickedLine));
                }
                if (body.lineItemsComplete) {
                    // Last item of the line — celebratory cue. Always
                    // wait for BE confirmation here: the celebratory
                    // sound is the picker's signal that the *whole
                    // line* is done (different from a mid-line tick),
                    // and downstream state (JIT order append, wave
                    // complete) can't be pre-celebrated.
                    //
                    // Batch-sibling aware: if the BE's next current line
                    // is the same SKU at the same location (a split-batch
                    // SKU, one wave line per inventory batch), the hero's
                    // combined counter is NOT done — tick, don't
                    // celebrate, or the picker hears "move on" and then
                    // feels sent back (2026-10-06, wave 10112).
                    // pickedLine carries sku but not location; a picked
                    // line is always at the pre-swap hero's location
                    // (sibling picks are same-location by definition).
                    const justPicked = body.pickedLine ?? swapSnapshot.currentLine;
                    const justPickedLoc = swapSnapshot.currentLine?.location;
                    const nextIsBatchSibling = !!body.currentLine && !!justPicked
                        && body.currentLine.line_id !== justPicked.line_id
                        && body.currentLine.sku === justPicked.sku
                        && body.currentLine.location === justPickedLoc;
                    if (nextIsBatchSibling) playBoxAdd(); else playSuccess();
                    flash("success");
                    if (body.waveComplete) {
                        scheduleWaveCompleteNavigate();
                    } else if (body.advancedLineId !== undefined && body.currentLine) {
                        // Honour Require Location Scan, but skip the
                        // re-scan when the next line is at the same
                        // physical location as the one just completed
                        // (the picker hasn't moved). Compare against
                        // the pre-swap snapshot so we measure "was the
                        // line just picked at the same place as the
                        // new current line?" — `currentLine` state may
                        // already reflect the optimistic swap.
                        const prevLoc = swapSnapshot.currentLine?.location;
                        const sameLoc = body.currentLine.location === prevLoc;
                        if (sameLoc || !wave.require_location_scan) {
                            setPhase("awaiting-items");
                        } else {
                            setPhase("awaiting-location");
                        }
                    }
                } else if (!didOptimisticSwap) {
                    // Accepted scan that skipped the optimistic path —
                    // an item from a sibling line at this location
                    // (scan-any-item-at-location) or a barcode not
                    // carried on the line (master SKU). This is its
                    // first positive feedback, so fire the cue now.
                    // applyResponse() above already moved the qty on
                    // the "Also at this location" card / progress bar.
                    playBoxAdd();
                    flash("success");
                }
                // Current-line mid-line item scans fire the success cue
                // optimistically at the top of submitScan (see
                // qtyRemaining > 1 branch), so we deliberately don't
                // double-fire here on confirmation. The BE's
                // applyResponse() has already corrected qty and updated
                // the lookahead queue.
            }
        } catch {
            if (didOptimisticSwap) {
                setCurrentLine(swapSnapshot.currentLine);
                setLookaheadQueue(swapSnapshot.lookaheadQueue);
                setPhase(swapSnapshot.phase);
                setLastPicked(swapSnapshot.lastPicked);
            }
            playError();
            flash("error");
            showToast("error", t("server_error"), { toastId: "picking-scan-err" });
        } finally {
            setSubmitting(false);
            setScanValue("");
            // Drain one queued scan if present. setTimeout(0) lets
            // React commit the state updates from this run so the next
            // call reads fresh currentLine / lookaheadQueue / phase.
            //
            // KEEP inFlightRef=true while there's a queued drain. If
            // we released it here, a fresh keydown landing as its own
            // macrotask BEFORE the setTimeout fires would see
            // inFlightRef=false, run immediately, and jump ahead of
            // the queued scan — producing out-of-order processing
            // ("Scan location first" / "Item not in this line"
            // rejections when the picker scans through a location
            // transition fast). The drain's setTimeout runs in one
            // task that releases the flag *and* invokes the next
            // submitScan synchronously, so no other JS can interleave
            // between the release and the next scan's start.
            const next = scanQueueRef.current.shift();
            if (next) {
                setTimeout(() => {
                    inFlightRef.current = false;
                    submitScanRef.current(next);
                }, 0);
            } else {
                inFlightRef.current = false;
            }
        }
    }, [phase, scanValue, wave.wave_id, missing, applyResponse, submitSkipScan, flash, t, navigate, onWaveComplete, currentLine, lookaheadQueue, currentToteId, wave.require_location_scan, localFirst, session, lastPicked, confirmSwapAnyway, dismissSwapGuard]);

    // Keep the ref in sync so the `finally` drain always recurses into
    // the latest closure (deps above can re-create submitScan).
    useEffect(() => { submitScanRef.current = submitScan; }, [submitScan]);

    // Drop any queued scans if the wave wraps up so a late burst doesn't
    // fire scans against a no-longer-active wave.
    useEffect(() => {
        if (phase === "wave-complete") {
            scanQueueRef.current = [];
        }
    }, [phase]);

    // Hero values — fall back to em-dashes if currentLine is null
    // (shouldn't render in that case; wave-complete state takes over).
    // Units still owed per aisle, in walk order — the "Units left by
    // aisle" strip. Local-first reads the session's live lines (moved by
    // every accepted pick, skip re-anchor, missing write-off and JIT
    // append); legacy reads the mirror applyResponse maintains.
    const aisleLines = localFirst ? session.lines : legacyLines;
    const aisleUnits = useMemo(
        () => aisleUnitsFor(aisleLines, currentLine, wave.warehouse),
        [aisleLines, currentLine, wave.warehouse],
    );

    // Prefer location_display (aisle-bay-level) over the raw barcode
    // so the picker sees just the where-to-walk bit, not the
    // warehouse + zone prefix.
    const location = currentLine?.location_display ?? currentLine?.location ?? "—";
    const qtyRemaining = currentLine ? Math.max(0, currentLine.qty_required - currentLine.qty_picked) : 0;

    // Sub-prompt picks i18n key from phase
    const subPrompt = (() => {
        if (phase === "awaiting-resume-tote") return t("picking_sub_scan_resume_tote", { tote: pendingResumeTote ?? "" });
        if (phase === "awaiting-starting-location") return t("picking_sub_scan_starting_location");
        if (phase === "awaiting-first-tote") {
            // Large: after the first order, the fresh-tote prompt means
            // "your next ORDER starts here" — say so, or the picker
            // reads it as the same order continuing into another tote
            // (reported 2026-08-18 as a suspected bad merge).
            if (wave.pick_type === "large" && progress.itemsPicked > 0) {
                return t("picking_sub_scan_tote_next_order");
            }
            // Pick & Sort is picked into york cages only (the BE
            // refuses any other tote type with tote_not_york), so the
            // command names the container the picker must fetch. Pembroke
            // Singles use yorks as well.
            if (picksIntoYork) return t("picking_sub_scan_york");
            return t("picking_sub_scan_tote");
        }
        if (phase === "awaiting-location") return t("picking_sub_scan_location");
        if (phase === "awaiting-items") {
            if (qtyRemaining === 1) return t("picking_sub_scan_items_one");
            return t("picking_sub_scan_items", { qty: String(qtyRemaining) });
        }
        if (phase === "missing-location" && missing) {
            return t("picking_missing_loc_required_pre", { location: missing.line.location });
        }
        if (phase === "missing-counting") return t("picking_missing_cmd_scan_items");
        return "";
    })();

    // Large waves: the counter and bar are scoped to the CURRENT order
    // (the BE sends order* fields only for large). The wave-level
    // numbers keep flowing underneath for the recordPick effect and
    // completion logic; showing them raw confused pickers when the JIT
    // lookahead appended the next order's units mid-pick ("37/71" on a
    // 37-unit order).
    const displayTotalItems  = progress.orderTotalItems  ?? progress.totalItems;
    const displayItemsPicked = progress.orderItemsPicked ?? progress.itemsPicked;
    const progressPct = displayTotalItems > 0
        ? (displayItemsPicked / displayTotalItems) * 100
        : 0;

    // Large next-order prompt: the wave (= one order) is complete; the
    // picker scans wherever they NOW stand to open the next order under
    // the same filters (read off the wave header, so they survive a
    // reload), or taps Done to stop. A successful start refetches the
    // active wave — picking.tsx keys this screen by wave_id, so the new
    // wave mounts fresh.
    // Large: "How full is your york?" — the space left decides which
    // orders the server will offer next. 4/4 skips the wave start and
    // goes straight to the drop zone; part-full falls through to the
    // unchanged scan-location prompt carrying the answer; EMPTY goes
    // back to the Pick Queue so the Suggested card decides the next
    // pick (yorkFlow.ts — Alex, 2026-10-05: fill the york before
    // switching, but never stay on Large all day when Sorting is now
    // the suggestion).
    if (phase === "york-fill") {
        return (
            <YorkFillPrompt
                onAnswer={(quarters) => {
                    setYorkQuarters(quarters);
                    const next = afterYorkAnswer(quarters);
                    if (next === "york-drop") {
                        setYorkDropReason("full");
                        setPhase("york-drop");
                        return;
                    }
                    if (next === "queue") {
                        onWaveComplete();
                        navigate("/picking", { replace: true });
                        return;
                    }
                    setPhase("next-order");
                }}
            />
        );
    }

    // Large: take the york back. Two ways in — the picker said 4/4, or
    // the server said nothing left fits the space they reported. Either
    // way the next order allocates against an empty york.
    if (phase === "york-drop") {
        return (
            <YorkDropOff
                reason={yorkDropReason}
                quarters={yorkQuarters}
                // Opens the york_drops row as the screen appears, so
                // started_at predates the walk we are trying to measure.
                onShown={async () => {
                    yorkDropIdRef.current = await openYorkDrop(yorkDropReason, {
                        waveId: wave.wave_id,
                        fillQuarters: yorkQuarters,
                    });
                }}
                onConfirm={async () => {
                    await confirmYorkDrop(yorkDropIdRef.current);
                    yorkDropIdRef.current = null;
                    // An empty york again. Back to the Pick Queue rather
                    // than straight into another Large order: the drop
                    // is the moment to re-check the suggestion (yorkFlow
                    // .ts). If Large is still right the card is one tap
                    // and the same starting-location scan as before.
                    setYorkQuarters(0);
                    if (afterYorkDrop() === "queue") {
                        onWaveComplete();
                        navigate("/picking", { replace: true });
                        return;
                    }
                    setPhase("next-order");
                }}
            />
        );
    }

    if (phase === "next-order") {
        return (
            <LargeAnchorScan
                prompt={t("picking_next_order_scan_location")}
                exitLabel={t("picking_anchor_done")}
                onExit={() => {
                    onWaveComplete();
                    navigate("/picking", { replace: true });
                }}
                onScan={async (barcode) => {
                    const outcome = await startLargeWaveAt(barcode, {
                        tier: wave.tier,
                        ageFilter: wave.age_filter ?? "all",
                        deliveryFilter: wave.delivery_filter ?? "all",
                        channelFilter: wave.channel_filter ?? "all",
                        ageFrom: wave.age_from ?? null,
                        ageTo: wave.age_to ?? null,
                        dueFilter: wave.due_filter === true,
                        pickQueue: wave.pick_queue ?? null,
                        yorkFillQuarters: yorkQuarters,
                    }, t);
                    if (outcome.kind === "started") onWaveComplete();
                    // Good scan, queued orders, no room for any of them.
                    // Send them to the drop zone with the reason shown,
                    // rather than the "no orders" beat.
                    if (outcome.kind === "york_full") {
                        setYorkDropReason("no_fit");
                        setPhase("york-drop");
                    }
                    return outcome;
                }}
            />
        );
    }

    // Wave finished. Two beats: the amber tick, then a "Wave complete"
    // message + icon (same tick→message treatment as the Drop modules'
    // "ALL CAUGHT UP"), before bouncing back to the pick list. Applies
    // to every pick type (Large / Single / Pick & Sort).
    if (phase === "wave-complete") {
        return (
            <div className="scan-panel picking-active">
                <div className="picking-complete">
                    <div className="picking-complete-stage">
                        {waveCompleteStage === "tick" ? (
                            <ScanStatusIcons status="complete" />
                        ) : (
                            <div className="picking-wave-complete">
                                <PackageCheck className="picking-wave-complete-icon" strokeWidth={1.5} />
                                <div className="picking-wave-complete-msg">{t("picking_wave_complete")}</div>
                            </div>
                        )}
                    </div>
                </div>
                <HiddenScanInput
                    value={scanValue}
                    onChange={setScanValue}
                    onSubmit={() => { /* no-op while transitioning out */ }}
                    disabled
                    disableCamera
                    hideToggles
                />
            </div>
        );
    }

    // Large order-complete: a transient amber tick between orders. The
    // beginOrderCompleteTote timer flips this to the Scan Tote prompt
    // (or defers to wave-complete if that was the last order).
    if (phase === "order-complete") {
        return (
            <div className="scan-panel picking-active">
                <div className="picking-complete">
                    <ScanStatusIcons status="complete" />
                </div>
                <HiddenScanInput
                    value={scanValue}
                    onChange={setScanValue}
                    onSubmit={() => { /* no-op while transitioning out */ }}
                    disabled
                    disableCamera
                    hideToggles
                />
            </div>
        );
    }

    // The unified PickingScreen carries its own progress bar + top bar
    // — render the legacy chrome only for the simplified phases
    // (missing, awaiting-starting-location, awaiting-first-tote) that
    // still use the old hero layout.
    const useUnifiedScreen =
        currentLine !== null
        && swapGuard === null
        && phase !== "missing-location"
        && phase !== "missing-counting"
        && phase !== "missing-resolved"
        && phase !== "awaiting-resume-tote"
        && phase !== "awaiting-starting-location"
        && phase !== "awaiting-first-tote";

    // Demo panel — phase-aware buttons that simulate a scan by calling
    // submitScan with the right barcode, so the picker flow can be
    // exercised without a physical scanner. Reused both inside
    // PickingScreen's aboveActionBar slot (for the unified phases, so
    // it sits between the bay diagram and the dark action bar) and as
    // a sibling below the legacy hero for the simplified phases.
    const demoPanel = demo ? (
        <div className="book-in-demo-panel">
            <span className="book-in-demo-label">Demo</span>

            {phase === "awaiting-starting-location" && (
                <>
                    {[
                        "NP-A-1-1-1",  // walk_order 10
                        "NP-A-1-1-2",  // walk_order 20
                        "NP-A-1-2-1",  // walk_order 30
                        "NP-A-1-2-2",  // walk_order 40
                        "NP-A-2-1-1",  // walk_order 50
                    ].map(loc => (
                        <button
                            key={loc}
                            className="book-in-demo-btn"
                            onClick={() => submitScan(loc)}
                            title="Scan this as your starting location"
                        >
                            {loc}
                        </button>
                    ))}
                    <button
                        className="book-in-demo-btn book-in-demo-btn--bad"
                        onClick={() => submitScan("WRONG-LOC")}
                        title="Should be rejected as invalid_starting_location"
                    >
                        wrong
                    </button>
                </>
            )}

            {phase === "awaiting-location" && currentLine && (
                <>
                    <button
                        className="book-in-demo-btn"
                        onClick={() => submitScan(currentLine.location)}
                    >
                        {currentLine.location}
                    </button>
                    <button
                        className="book-in-demo-btn book-in-demo-btn--bad"
                        onClick={() => submitScan("WRONG-LOC")}
                        title="Scan a wrong location"
                    >
                        wrong
                    </button>
                </>
            )}

            {phase === "awaiting-items" && currentLine && (
                <>
                    <button
                        className="book-in-demo-btn"
                        onClick={() => submitScan(currentLine.sku)}
                    >
                        {currentLine.sku}
                    </button>
                    <button
                        className="book-in-demo-btn book-in-demo-btn--bad"
                        onClick={() => submitScan("WRONG-SKU")}
                        title="Scan a wrong SKU"
                    >
                        wrong
                    </button>
                </>
            )}

            {(phase === "awaiting-starting-location"
                || phase === "awaiting-first-tote"
                || phase === "awaiting-location"
                || phase === "awaiting-items") && (
                <>
                    {["10000", "10001", "10002", "10003", "10004", "10005", "10006", "10007"].map(toteBc => (
                        <button
                            key={toteBc}
                            className="book-in-demo-btn book-in-demo-btn--tote"
                            onClick={() => submitScan(toteBc)}
                        >
                            {toteBc}
                        </button>
                    ))}
                </>
            )}

            {phase === "missing-location" && missing && (
                <>
                    <button
                        className="book-in-demo-btn"
                        onClick={() => submitScan(missing.line.location)}
                    >
                        {missing.line.location}
                    </button>
                    <button
                        className="book-in-demo-btn book-in-demo-btn--bad"
                        onClick={() => submitScan("WRONG-LOC")}
                    >
                        wrong
                    </button>
                </>
            )}

            {phase === "missing-counting" && missing && (
                <>
                    {missing.expected.map(e => (
                        <button
                            key={e.sku}
                            className="book-in-demo-btn"
                            onClick={() => submitScan(e.sku)}
                        >
                            {e.sku}
                        </button>
                    ))}
                    <button
                        className="book-in-demo-btn book-in-demo-btn--bad"
                        onClick={() => submitScan("UNKNOWN-SKU-XYZ")}
                        title="Scan a barcode that resolves to nothing"
                    >
                        unknown
                    </button>
                </>
            )}
        </div>
    ) : null;

    // Roll-cage estimate for the Large order the picker is on — how
    // many cages to fetch before they start, from the summed volume of
    // that order's lines, for the card under the SCAN TOTE prompt.
    // Scoped with the SAME orderScopeFor the item counter uses, so the
    // two can never disagree about which order is on screen. Null for
    // other pick types (they share cages across a whole wave, so a
    // per-order figure would mean nothing), when the warehouse has the
    // estimate switched off, and in the anchor window before any line
    // has arrived.
    //
    // Computed inline rather than memoised: this sits below the
    // phase early-returns, where a hook can't go. It's one pass over
    // the wave's lines (tens, not thousands, for a Large order).
    const rollCageOrderId = wave.pick_type === "large"
        ? orderScopeFor(session.lines, currentLine)
        : null;
    const rollCageEstimate = rollCageOrderId == null ? null : estimateRollCages(
        linesForOrder(session.lines, rollCageOrderId),
        wave.picking_roll_cage_utilisation_pct,
        wave.roll_cage_cbm,
    );

    // Pick-type strip under the progress line — Single / Pick & Sort /
    // Putwall / Large — so the picker can tell which flow they are in
    // without reading the hero. Same on every phase, like the extras.
    const pickTypeKind = pickTypeKindFor(wave);
    // Paired wave: the strip also says which half this is and who is on
    // the other one ("Pick & Sort · Segment 1 of 2 · Rhys on 2"), or that
    // the other half is still waiting for a picker.
    const pickTypeLabel = (wave.segment_count ?? 1) > 1
        ? `${t(pickTypeLabelKeyFor(wave))} · ${t("picking_segment_badge", { no: String(wave.segment_no ?? 1), of: String(wave.segment_count) })}`
            + (wave.sibling_awaiting_picker
                ? ` · ${t("picking_segment_sibling_waiting")}`
                : wave.sibling_picker_name
                    ? ` · ${t("picking_segment_sibling_on", { name: wave.sibling_picker_name, no: String((wave.segment_no ?? 1) === 1 ? 2 : 1) })}`
                    : "")
        : t(pickTypeLabelKeyFor(wave));

    // Top-bar extras (tier badge + open-tote pill) shared between the
    // unified PickingScreen and the simplified-phase PickingTopBar so
    // the picker has consistent context across all phases.
    const topBarExtras = (
        <>
            <span className="picking-tier-badge">
                {t((currentLine?.location_high_low ?? wave.tier) === "high"
                    ? "picking_tier_high"
                    : "picking_tier_low")}
            </span>
            {currentToteId && (
                <span className="picking-open-tote" title={t("picking_open_tote_label")}>
                    <PackageOpen size={16} className="picking-open-tote-icon" />
                    <span className="picking-open-tote-id">{currentToteId}</span>
                </span>
            )}
        </>
    );

    return (
        <div className="scan-panel picking-active">
            {/* Offline / stalled-sync banner. The local-first path keeps
                accepting scans (green beeps, items into the tote) while
                the device is offline — this is the ONLY thing telling
                the picker those scans are parked on the device rather
                than saved. Stays up until every scan has synced. */}
            {showSyncBanner && (
                <div className="picking-sync-banner" role="status">
                    <WifiOff className="picking-sync-banner-icon" strokeWidth={2} />
                    <span>
                        {session.syncBacklog > 0
                            ? t("picking_sync_pending_banner", { count: String(session.syncBacklog) })
                            : t("picking_sync_offline_banner")}
                    </span>
                </div>
            )}
            {/* Simplified phases (starting-location, first-tote,
                missing) render the new ps-bar above their hero so the
                picker always has pause + back + alert buttons reachable.
                The unified PickingScreen path renders its own
                PickingTopBar internally. */}
            {!useUnifiedScreen && (
                <PickingTopBar
                    itemsTotal={displayTotalItems}
                    itemsPicked={displayItemsPicked}
                    pickTypeLabel={pickTypeLabel}
                    pickTypeKind={pickTypeKind}
                    barExtras={topBarExtras}
                    onPause={onPauseTap}
                    onAlert={(phase === "awaiting-location" || phase === "awaiting-items") ? startMissingFlow : undefined}
                    onBack={() => pauseAndExit(false)}
                    pauseDisabled={submitting || pausing}
                    // Held while local-first scans are still syncing: /missing/start
                    // reads the BE's qty_picked, so opening a count on top of an
                    // unsynced pick would investigate units the picker already has
                    // in their tote. Same guard the manual pick uses.
                    alertDisabled={submitting || !currentLine
                        || (localFirst && session.syncBacklog > 0)}
                    backDisabled={submitting || pausing}
                />
            )}
            {swapGuard ? (
                /* Singles: the york swap would split the current order.
                   Show what is left of it; the picker keeps picking or
                   swaps anyway (or scans the same york again). */
                <PartPickedSwapCard
                    order={swapGuard.order}
                    newToteId={swapGuard.barcode}
                    busy={swapForcing}
                    onKeepPicking={dismissSwapGuard}
                    onSwapAnyway={() => { void confirmSwapAnyway(); }}
                />
            ) : phase === "missing-resolved" && resolved ? (
                /* Outcome of the count — the one screen that says what to do
                   next. Dismissed by the picker (continueFromResolution), not
                   by a timer, so the instruction can't be missed. */
                <MissingResolutionCard
                    resolution={resolved}
                    busy={submitting}
                    onContinue={continueFromResolution}
                />
            ) : phase === "missing-location" && missing ? (
                /* Gate: prove they're at the right shelf before a single item
                   is counted. Nothing else on screen until it's scanned. */
                <MissingLocationGate
                    location={missing.line.location}
                    locationDisplayName={missing.locationDisplayName}
                    sku={missing.line.sku}
                    qtyOutstanding={missingQtyOutstanding}
                    flashHidden={scanStatus === "error"}
                    busy={submitting}
                    onCancel={cancelMissingFlow}
                />
            ) : phase === "missing-counting" && missing ? (
                <div className={"picking-missing-screen" + (scanStatus === "error" ? " scan-flash-hidden" : "")}>
                    <MissingItemCountList
                        targetSku={missing.line.sku}
                        location={missing.line.location}
                        locationDisplayName={missing.locationDisplayName}
                        expected={missing.expected}
                        counts={missing.counts}
                        stockCheckLines={missing.stockCheckLines}
                        pickRelevant={missing.pickRelevant}
                    />
                    <div className="picking-sub-prompt">{subPrompt}</div>
                    <div className="picking-missing-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={cancelMissingFlow}
                            disabled={submitting}
                        >
                            {t("picking_missing_cancel")}
                        </button>
                        {/* Once the count has turned up everything the line
                            owes, the finish button says so — the picker is
                            holding the item and shouldn't have to work out
                            from a row's tick that they can stop counting. */}
                        <button
                            type="button"
                            className={"btn " + (missingTargetFound ? "btn-success" : "btn-primary")}
                            onClick={() => setFinishConfirm(true)}
                            disabled={submitting}
                        >
                            {submitting
                                ? t("picking_missing_finishing")
                                : missingTargetFound
                                    ? t("picking_missing_done_found")
                                    : t("picking_missing_done")}
                        </button>
                    </div>

                    {/* Finish confirm — states what the count found and what
                        resolving it will do, and asks about the rest of the
                        shelf. Same shape as the Stock Check module's own
                        complete confirm. */}
                    {finishConfirm && (
                        <MissingFinishConfirm
                            location={missing.locationDisplayName || missing.line.location}
                            sku={missing.line.sku}
                            foundQty={missing.counts[missing.line.sku] ?? 0}
                            requiredQty={missingQtyOutstanding}
                            uncounted={uncountedLines}
                            busy={submitting}
                            onCancel={() => setFinishConfirm(false)}
                            onConfirm={finishMissingFlow}
                        />
                    )}

                    {/* Surplus found during the count. The count itself never
                        creates stock — booking in is the picker's decision,
                        and goes through the Stock Check module's Add so the
                        unit is costed from the catalogue. */}
                    {surplusPrompt && (
                        <div className="modal-overlay" onClick={() => !addingSurplus && setSurplusPrompt(null)}>
                            <div className="modal-card" onClick={e => e.stopPropagation()}>
                                <div className="modal-card-header">
                                    <h2>{t(surplusPrompt.extraUnit
                                        ? "picking_missing_extra_unit_title"
                                        : "picking_missing_surplus_title")}</h2>
                                </div>
                                <div className="modal-card-body">
                                    <p className="stock-check-modal-hint">
                                        {t(surplusPrompt.extraUnit
                                            ? "picking_missing_extra_unit_body"
                                            : "picking_missing_surplus_body", { sku: surplusPrompt.sku })}
                                    </p>
                                    <div className="modal-confirm-buttons">
                                        <button
                                            type="button"
                                            className="btn btn-ghost btn-lg"
                                            onClick={() => setSurplusPrompt(null)}
                                            disabled={addingSurplus}
                                        >
                                            {t("no")}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-success btn-lg"
                                            onClick={confirmSurplusAdd}
                                            disabled={addingSurplus || !missing.stockCheckId}
                                        >
                                            {addingSurplus ? t("submitting") : t("yes")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : phase === "awaiting-resume-tote" ? (
                /* Resumed wave — the picker must re-scan the pause-time
                   tote before anything else registers: proves they
                   claimed the right wave AND hold its partially-picked
                   items. The prompt names the exact tote. */
                <ScanPromptHero prompt={subPrompt} flashHidden={scanStatus === "error"}>
                    <div className="picking-hero-ticks">
                        <ScanStatusIcons status={scanStatus === "complete" ? "complete" : "idle"} />
                    </div>
                </ScanPromptHero>
            ) : phase === "awaiting-starting-location" ? (
                /* Large waves wait for a starting-location scan
                   before doing anything else. Same simplified hero
                   shape as awaiting-first-tote, but the big icon is
                   the pin to cue "scan where you are". */
                <ScanPromptHero prompt={subPrompt} flashHidden={scanStatus === "error"}>
                    <div className="picking-hero-ticks">
                        {/* Per-scan green tick suppressed — audio cue is
                            the accepted-scan signal. Errors render via the
                            shared ScanFlashOverlay; only the amber
                            wave-complete tick shows here. */}
                        <ScanStatusIcons status={scanStatus === "complete" ? "complete" : "idle"} />
                    </div>
                </ScanPromptHero>
            ) : phase === "awaiting-first-tote" ? (
                /* Simplified hero — picker hasn't opened a tote yet.
                   No location / SKU / qty would land in anything, so
                   the only thing on screen is the SCAN TOTE prompt.
                   Order: icon (top) → sub-prompt (next-action) → tick
                   feedback (below), so the picker reads the command
                   before they see their scan acknowledged. Uses the
                   same PackageOpen icon as the header pill — same
                   visual language for "tote you can drop items into". */
                <ScanPromptHero
                    prompt={subPrompt}
                    flashHidden={scanStatus === "error"}
                    /* Tightens the hero's run-up so the card below the
                       command words stays clear of the fold on a
                       handheld. Only applied when there IS a card. */
                    className={rollCageEstimate ? "picking-hero--with-cages" : undefined}
                >
                    {/* Roll cages — Large only, and only here. This is the
                        one moment the number changes what the picker does:
                        they are standing still with the order just opened,
                        about to go and fetch cages. Once a tote is scanned
                        it is history, so it goes with the prompt rather
                        than following them down the pick. */}
                    {rollCageEstimate && <RollCageCard estimate={rollCageEstimate} />}
                    <div className="picking-hero-ticks">
                        {/* Per-scan green tick suppressed (see comment
                            above) — errors render via ScanFlashOverlay;
                            only the amber complete tick shows here. */}
                        <ScanStatusIcons status={scanStatus === "complete" ? "complete" : "idle"} />
                    </div>
                </ScanPromptHero>
            ) : currentLine ? (
                /* Main pick phase — unified PickingScreen used across
                   Single / Pick & Sort / Large / Shelf Drop / Forecast
                   Drop. Owns its own progress bar + top bar, so we
                   render it as a sibling of the missing/starting/tote
                   simplified heroes (which still use the old chrome).
                   Tick feedback is mounted absolutely-positioned over
                   the screen so the scan-status icon stays visible
                   without competing with the design's bottom action
                   bar. */
                <PickingScreen
                    priority={null}
                    orderRef={currentToteId ? `Tote ${currentToteId}` : `Wave ${wave.wave_id}`}
                    itemsTotal={displayTotalItems}
                    itemsPicked={displayItemsPicked}
                    pickTypeLabel={pickTypeLabel}
                    pickTypeKind={pickTypeKind}
                    location={location}
                    // Skip-location: tap the pin → scan the location
                    // you're moving to; bypassed locations rejoin at
                    // the end of the walk. Round-trip only — disabled
                    // offline and while local-first scans are syncing
                    // (a queued/replayed skip would reorder the wave
                    // long after the picker moved on).
                    onLocationTap={(phase === "awaiting-location" || phase === "awaiting-items")
                        ? () => setSkipPromptOpen(true)
                        : undefined}
                    locationTapDisabled={submitting || pausing || skipBusy || offline
                        || (localFirst && session.syncBacklog > 0)}
                    locationHighLow={currentLine.location_high_low ?? wave.tier}
                    bayPosition={currentLine.bay_position ?? null}
                    sku={currentLine.sku}
                    productTitle={currentLine.product_title ?? null}
                    productBarcode={currentLine.product_barcode ?? null}
                    secondaryBarcode={currentLine.product_barcode_secondary ?? null}
                    productBarcodes={currentLine.product_barcodes ?? null}
                    productImageUrl={currentLine.product_image_url ?? null}
                    productThumbnailUrl={currentLine.product_thumbnail_url ?? null}
                    qtyTotal={currentLine.group_qty_required ?? currentLine.qty_required}
                    qtyPicked={(currentLine.group_qty_prior_picked ?? 0) + currentLine.qty_picked}
                    showProductImage={wave.picking_show_product_image ?? true}
                    locationChangeMs={wave.picking_location_change_ms}
                    newLocationHighlightMs={wave.picking_new_location_highlight_ms}
                    otherSkusToShow={wave.picking_other_skus_count ?? 1}
                    otherSkusAtLocation={currentLine.other_lines_at_location ?? []}
                    locationQtyTotal={currentLine.location_qty_required ?? null}
                    locationQtyPicked={currentLine.location_qty_picked ?? null}
                    locationSkuCount={currentLine.location_sku_count ?? null}
                    aisleUnits={aisleUnits}
                    warehouse={wave.warehouse}
                    // Tap a location in the aisle popup → the same
                    // skip-to-location round-trip as the pin, with the
                    // row's barcode standing in for the scan. Same gate
                    // (scan hero only, online, nothing left to sync) —
                    // see onLocationTap above.
                    onAisleLocationSelect={(phase === "awaiting-location" || phase === "awaiting-items")
                        ? (location) => { void submitSkipScan(location); }
                        : undefined}
                    aisleLocationSelectDisabled={submitting || pausing || skipBusy || offline
                        || (localFirst && session.syncBacklog > 0)}
                    pickSpeed={pickSpeed}
                    cycle={cycle}
                    scanTarget={
                        phase === "awaiting-location" && wave.require_location_scan
                            ? "location"
                            : "barcode"
                    }
                    lastPicked={lastPicked}
                    onPause={onPauseTap}
                    onAlert={startMissingFlow}
                    onBack={() => pauseAndExit(false)}
                    pauseDisabled={submitting || pausing}
                    backDisabled={submitting || pausing}
                    // Held while local-first scans are still syncing: /missing/start
                    // reads the BE's qty_picked, so opening a count on top of an
                    // unsynced pick would investigate units the picker already has
                    // in their tote. Same guard the manual pick uses.
                    alertDisabled={submitting || !currentLine
                        || (localFirst && session.syncBacklog > 0)}
                    onManualPick={phase === "awaiting-items" ? () => setManualPickOpen(true) : undefined}
                    manualPickDisabled={submitting || manualBusy || pausing
                        || (localFirst && session.syncBacklog > 0)}
                    flashActive={scanStatus === "error"}
                    barExtras={topBarExtras}
                    aboveActionBar={demoPanel}
                />
            ) : null}
            {/* Bad-scan feedback — full-viewport red wash + X rendered
                ABOVE every layout layer, identical across all five
                picking workflows (see ScanFlashOverlay). Per-scan green
                tick is suppressed everywhere — audio + the hero updating
                is the accepted-scan signal; amber "complete" ticks render
                in their own phase layouts. */}
            <ScanFlashOverlay active={scanStatus === "error"} />
            {useUnifiedScreen && scanStatus === "complete" && (
                <div className="picking-floating-ticks">
                    <ScanStatusIcons status="complete" />
                </div>
            )}

            {/* Manual pick ("barcode not scanning") — confirm the hero
                line's quantity without a scan. Rendered only while the
                hand button opened it; the scanner is gated below so a
                stray trigger pull can't advance state under the modal. */}
            {manualPickOpen && currentLine && (
                <ManualPickModal
                    // Keyed on the line so a hero swap under the open
                    // modal (background reconcile, late queued scan)
                    // remounts it — the qty selection resets against the
                    // NEW line instead of silently retargeting.
                    key={currentLine.line_id}
                    item={{
                        sku: currentLine.sku,
                        title: currentLine.product_title ?? null,
                        thumbnailUrl: currentLine.product_thumbnail_url ?? null,
                        imageUrl: currentLine.product_image_url ?? null,
                        maxQty: qtyRemaining,
                    }}
                    busy={manualBusy}
                    onConfirm={confirmManualPick}
                    onClose={() => setManualPickOpen(false)}
                />
            )}

            {/* Pause / Close wave choice (Singles only) — see onPauseTap.
                Pause here is the plain pause; Close wave previews the
                done / untouched split before committing, or lists the
                part-picked orders that block it. */}
            {closeModal && (
                <CloseWaveModal
                    state={closeModal}
                    busy={closing || pausing}
                    onPause={() => { setCloseModal(null); void pauseAndExit(false); }}
                    onRequestClose={requestCloseWave}
                    onConfirmClose={confirmCloseWave}
                    onCancel={() => { if (!closing) setCloseModal(null); }}
                />
            )}

            {/* Skip-location prompt — the next scan is routed to the
                skip endpoint (see submitScan's interception), so the
                modal itself only carries the instruction + escape
                hatch. Scanning a location behind the current position
                is a harmless no-op by design. */}
            {skipPromptOpen && (
                <div className="modal-overlay" onClick={() => !skipBusy && setSkipPromptOpen(false)}>
                    <div className="modal-card" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("picking_skip_title")}</h2>
                        </div>
                        <div className="modal-card-body">
                            <p className="stock-check-modal-hint">{t("picking_skip_body")}</p>
                            <div className="modal-confirm-buttons">
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => setSkipPromptOpen(false)}
                                    disabled={skipBusy}
                                >
                                    {t("cancel")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={submitScan}
                // Deliberately NOT disabled on `submitting`. The
                // global keydown listener short-circuits on `disabled`,
                // which would drop fast burst scans while a previous
                // one is mid-flight. submitScan's inFlightRef +
                // scanQueueRef pair serialises the bursts instead —
                // queued scans drain in the previous call's `finally`,
                // so nothing gets lost. Same pattern as book-in.tsx.
                // The manual-pick modal DOES disable it (same as
                // move-stock): a scan mid-confirm could advance the
                // line under the picker's decision. Likewise the surplus
                // "add this?" prompt — a scan landing behind it would be
                // counted against a decision the picker hasn't made yet.
                // The finish confirm and the outcome card both own the
                // screen: a scan landing behind either would be counted
                // against a decision the picker hasn't made yet, or silently
                // swallowed while they read what to do next.
                disabled={manualPickOpen || surplusPrompt !== null || closeModal !== null
                    || finishConfirm || phase === "missing-resolved"}
                disableCamera
                hideToggles
            />

            {/* Legacy phases (missing, starting-location, first-tote)
                still render the old hero — show the demo panel here as
                a sibling. For unified-screen phases the demo panel is
                passed via PickingScreen's aboveActionBar prop instead
                so it sits between the bay diagram and the dark action
                bar (both visible). */}
            {!useUnifiedScreen && demoPanel}
        </div>
    );
};

export default ActiveWaveScreen;
